<script setup>
import axios from 'axios';
import { computed, defineProps, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { debounce } from 'lodash';
import { getExportName } from '../../helpers/helper';
import { plural } from '../../helpers/plural';
import { removedToast, sentToast } from '../../helpers/notRegisteredRow';
import { latestOnly, pageOrLast, tabFor } from '../../helpers/accessPages';
import { useSocketStore } from '../../stores/socketStore';
import { useAuthStore } from '../../stores/auth';
import { useBrokerScopeStore } from '../../stores/brokerScope';
import { useCreateOnArrival } from '../../composables/useCreateOnArrival';
import { viewingPolicyholders } from '../../composables/useHubBadges';
import { useSectionHome } from '../../composables/useUnsavedChanges';
import Loading from '../Loading.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import { longDate } from '../../helpers/usDate';
import FormGroup from '../forms/FormGroup.vue';
import IconDefaultAvatar from '../icons/IconDefaultAvatar.vue';
import UploadForMemberModal from '../resource-access/UploadForMemberModal.vue';
import useHubMembers from '../../composables/useHubMembers';
import { answerOptions, published, validateName } from '../../helpers/policyLines';
import { useStagedMemberResources } from '../../composables/useStagedMemberResources';
import BrokerScopePicker from '../policyholders/BrokerScopePicker.vue';
import PolicyholderKpiRow from '../policyholders/PolicyholderKpiRow.vue';
import PolicyholderTable from '../policyholders/PolicyholderTable.vue';
import ExportListModal from '../policyholders/ExportListModal.vue';
import PolicyholderBulkBar from '../policyholders/PolicyholderBulkBar.vue';
import UnassignedBrokerBanner from '../policyholders/UnassignedBrokerBanner.vue';
import ReleasedBookNotice from '../policyholders/ReleasedBookNotice.vue';
import PolicyholderInvitations from '../policyholders/PolicyholderInvitations.vue';
import PolicyholderRequests from '../policyholders/PolicyholderRequests.vue';
import XlTabs from '../xl/XlTabs.vue';
import XlCard from '../xl/XlCard.vue';
import XlButton from '../xl/XlButton.vue';
import XlSearchField from '../xl/XlSearchField.vue';
import XlMultiSelect from '../xl/XlMultiSelect.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlField from '../xl/XlField.vue';
import XlPagination from '../xl/XlPagination.vue';
import XlModal from '../xl/XlModal.vue';
import PageHeader from "../PageHeader.vue";
import { findDifferencesInObjects } from './../../helpers/helper.js';
import FormDropdownGroup from './../../pge-components/pge-dropdown/pge-dropdown-group.vue';
import FormDropdown from './../../pge-components/pge-dropdown/pge-dropdown.vue';
import TooltipInfo from './../../pge-components/pge-info-tooltip.vue';
import PgeMultiselect from './../../pge-components/pge-multiselect.vue';
import useSimplePagination from '../../composables/useSimplePagination';
import LoadingOverlay from '../LoadingOverlay.vue';
import IconUserRemove from '../icons/IconUserRemove.vue';
import IconCopy from '../icons/IconCopy.vue';
import { isValidEmail } from '../../helpers/helper';
import BrokerCard from '../policyholders/BrokerCard.vue';
import BulkAssignModal from '../policyholders/BulkAssignModal.vue';
import BrokerPicker from '../policyholders/BrokerPicker.vue';
import {
  assignBroker,
  listBrokers,
  matchingPolicyholders,
  transferBook,
  undoBulkAssign,
} from '../../api/policyholders';
import { canAssignBrokers as roleCanAssignBrokers, hubReader, isBroker as roleIsBroker } from '../../helpers/roles';
import { hubEmptyState, hubName } from '../../helpers/hubEmptyStates';
import { useEventStore } from '../../stores/event';
import XlEmptyState from '../xl/XlEmptyState.vue';

defineOptions({ name: 'UsersView' });

const store = window.store;

const props = defineProps({
  mini: {
    type: Boolean,
    default: false
  }
});

const accessRequestsTabs = computed(() => (viewerIsBroker.value
  ? []
  : [{ id: 4, label: 'Access requests', count: pendingCount(accessCounts.value.pending_requests ?? 0) }]));

const USER_MODAL_TABS = computed(() => [
  { id: 1, label: 'Registered', count: pendingCount(registeredHubTotal.value ?? 0) },
  { id: 2, label: 'Not registered', count: pendingCount(accessCounts.value.not_registered ?? 0) },
  ...accessRequestsTabs.value,
]);
const isAllSelected = ref(false);
const isHalfMarked = ref(false);
const exportAllLoading = ref(false);
const searchByName = ref('');
const selectedUsers = ref([]);
const filters = ref({});
const fields = ref([]);
const registeredAllUsers = ref([]);
const registeredHubTotal = ref(0);
// Until the first counts arrive the cards and tabs hold their numbers' places
// rather than showing noughts that are not true.
const countsReady = ref(false);
const countsFailure = ref(null);
const countsBroker = ref(null);
const countsLoading = computed(() => isLoading.value || !countsReady.value);
// As many placeholder rows as the page will show — a page is 10 (getData), or
// fewer once the hub's total says so — so the rows land where their
// placeholders were and nothing under the table moves ("Policyholders jumps":
// 8 placeholders became 10 rows, pushing everything below down).
const LIST_PAGE_SIZE = 10;
const loadingRowCount = computed(() => (countsReady.value && registeredHubTotal.value > 0
  ? Math.min(LIST_PAGE_SIZE, registeredHubTotal.value)
  : LIST_PAGE_SIZE));
// A figure space: the tab's count pill keeps a digit's width while it waits.
const pendingCount = (count) => (countsLoading.value ? '\u2007' : count);
const pendingAllUsers = ref([]);
const registeredUsers = reactive({
  data: [],
  total: 0,
  isLoading: false
});
const pendingUsers = reactive({
  data: [],
  total: 0,
  isLoading: false
});
const accessMembers = reactive({
  data: [],
  total: 0,
  currentPage: 1,
  lastPage: 1,
  isLoading: false,
});
const PENDING_TAB_STATUSES = 'invited,invitation_sent';
const accessRequests = reactive({ data: [], total: 0, currentPage: 1, lastPage: 1, isLoading: false });
const accessRequestsStatusFilter = ref('pending');
const accessCounts = ref({ invited: 0, registered: 0, invitation_sent: 0, pending_requests: 0, not_registered: 0, never_told: 0 });
const reviewRequestModal = ref(false);
const selectedRequest = ref(null);
const reviewLoading = ref(false);
const addMemberModal = ref(false);
const addMember = reactive({
  first_name: '',
  last_name: '',
  email: '',
  company: '',
  job_title: '',
  phone: '',
  broker_id: '',
  send_invitation: false,
  loading: false,
  errors: {},
});
const modalUser = ref(false);
const modalErase = ref({ visible: false, confirm: '', errors: false, loading: false });
const modalData = ref(null);
const isLoading = ref(true);
const loadCount = ref(0);
const saveUserLoading = ref(false);
const registerGroups = ref([]);
const prevFormData = ref(null);
const hasError = ref(false);
const hasChanged = ref(false);
const firstnameLimitExceedMessage = ref(false);
const lastnameLimitExceedMessage = ref(false);
const attendeesTooltipVisibility = ref(false);
const userProfileInfo = reactive({
  first_name: '',
  last_name: '',
  profile_image: null,
});
const pendingDeleteUserModal = reactive({
  visible: false,
  inviteId: 0,
});
const activeTab = ref(1);

// The tab is part of the address, because the Overview's counts link straight
// to one — "Never signed in" opens Not registered, not Registered. Named
// rather than numbered in the URL: the ids are an internal accident (there is
// no 3; a tab was removed) and would be a poor thing to publish.
const TAB_SLUGS = { registered: 1, pending: 2, requests: 4 };
const SLUG_FOR_TAB = { 1: 'registered', 2: 'pending', 4: 'requests' };

useSectionHome(() => { activeTab.value = TAB_SLUGS.registered; });

// --- Policy Line tagging -------------------------------------------------
// Tags assigned by Hub Staff to describe what a member is enrolled in.
// A tag the combobox creates is not persisted until Save, so the modal tracks
// two things: ids of existing tags, and labels of not-yet-created ones. The
// combobox holds one flat list of values where a pending tag is `new:<label>`.
const PENDING_TAG_PREFIX = 'new:';
const POLICY_LINE_EMPTY_STATE =
  "No policy lines yet. Type a name to create your hub's first one — for example Auto, Home or Life.";

const policyLineTags = ref([]);
// Until the tags are in, the policy-line filter keeps its place: it used to
// appear once they arrived and squeeze the search field, sliding the other
// filters 160px sideways ("Policyholders jumps"). Hidden only on a hub that
// turns out to have no policy lines.
const policyLineTagsLoaded = ref(false);
const policyLineNewTags = ref([]);
const policyLineSelected = ref([]);
const policyLineSavedSelection = ref([]);
const policyLineError = ref('');

const policyLineOptions = computed(() => [
  ...policyLineTags.value.map(tag => ({ value: tag.id, label: tag.name })),
  ...policyLineNewTags.value.map(label => ({ value: `${PENDING_TAG_PREFIX}${label}`, label })),
]);

// Colours come from the server so the chip a member sees on their own Profile is
// the same swatch staff see here. Pending tags have no server colour yet and
// fall back to the component's default until Save assigns one.
const policyLineChipColors = computed(() =>
  policyLineTags.value.reduce((acc, tag) => {
    acc[tag.id] = { color: tag.color, textColor: tag.text_color };
    return acc;
  }, {})
);

const policyLineFilterOptions = computed(() => policyLineTags.value.map(tag => tag.name));

// Save writes the broker (saveUser calls saveBroker), but the button that
// triggers it never counted a broker change as a change, so the assignment was
// unreachable: pick a broker and Save stays dead.
const brokerChanged = computed(() => brokerSelected.value !== prevBrokerSelected.value);

const policyLinesChanged = computed(() => {
  const key = (list) => [...list].map(String).sort().join('|');
  return key(policyLineSelected.value) !== key(policyLineSavedSelection.value);
});

const fetchPolicyLineTags = async () => {
  try {
    const res = await axios.get(`${store.event_backend_api}/policy-line-tags`);
    policyLineTags.value = published(res.data?.data?.tags ?? []);
  } catch (error) {
    console.error('Failed to load Policy Line tags:', error);
  } finally {
    policyLineTagsLoaded.value = true;
  }
};

// Charset and length, from the one place those rules live. The per-hub cap this
// also used to enforce is gone: XN-4535 made a policy line a page of its own,
// and an agency does not get to sell only twelve kinds of thing.
const validatePolicyLineLabel = (label) => validateName(label);

const handleCreatePolicyLine = (rawLabel) => {
  const label = String(rawLabel ?? '').trim().replace(/\s+/g, ' ');
  const normalized = label.toLowerCase();

  // The combobox already selects an exact match instead of emitting create, but
  // a second pending tag with the same name would slip past it.
  if (policyLineNewTags.value.some(existing => existing.toLowerCase() === normalized)) {
    return;
  }

  policyLineError.value = '';
  policyLineNewTags.value = [...policyLineNewTags.value, label];
  policyLineSelected.value = [...policyLineSelected.value, `${PENDING_TAG_PREFIX}${label}`];
};

const resetPolicyLineState = (user = null) => {
  const selection = (user?.policy_lines ?? []).map(tag => tag.id);
  policyLineSelected.value = selection;
  policyLineSavedSelection.value = [...selection];
  policyLineNewTags.value = [];
  policyLineError.value = '';
};

// Persist the modal's tag set. Existing tags go by id; labels created inline are
// created server-side in the same request so a save is one round trip.
const savePolicyLines = async (userId) => {
  if (!policyLinesChanged.value) return;

  await axios.put(`${store.event_backend_api}/users/${userId}/policy-line-tags`, {
    tag_ids: policyLineSelected.value.filter(value => typeof value === 'number'),
    new_tags: policyLineSelected.value
      .filter(value => typeof value === 'string' && value.startsWith(PENDING_TAG_PREFIX))
      .map(value => value.slice(PENDING_TAG_PREFIX.length)),
  });
};

const {
  pagination: registeredPagination,
  from: registeredFrom,
  to: registeredTo,
  setPage:
  setRegisteredPage,
  setTotal:
  setRegisteredTotal
} = useSimplePagination(10);
const {
  pagination: pendingPagination,
  setPage: setPendingPage,
  setTotal: setPendingTotal
} = useSimplePagination(10);
const {
  pagination: membersPagination,
  from: membersFrom,
  to: membersTo,
  setPage: setMembersPage,
  setTotal: setMembersTotal
} = useSimplePagination(10);
const {
  pagination: requestsPagination,
  from: requestsFrom,
  to: requestsTo,
  setPage: setRequestsPage,
  setTotal: setRequestsTotal
} = useSimplePagination(10);

// Paging must count what the filters matched.
const pagedTotal = computed(() => {
  if (activeTab.value === 2) return accessMembers.total ?? 0;
  if (activeTab.value === 4) return accessRequests.total ?? 0;
  return registeredUsers.total ?? 0;
});

watch(selectedUsers, (newVal) => {
  if (activeTab.value === 1) {
    isHalfMarked.value = newVal.length && newVal.length < registeredUsers.data.length;
    isAllSelected.value = newVal.length === registeredUsers.data.length ? newVal.length > 0 ? true : false : false;
  } else {
    isHalfMarked.value = newVal.length && newVal.length < accessMembers.data.length;
    isAllSelected.value = newVal.length === accessMembers.data.length ? newVal.length > 0 ? true : false : false;
  }
});

watch(() => registeredUsers.data, (newVal) => {
  selectedUsers.value = newVal.filter(user => user.selected);
}, { deep: true });

watch(() => accessMembers.data, (newVal) => {
  selectedUsers.value = newVal.filter(member => member.selected);
}, { deep: true });

watch(modalData, newVal => {
  let _hasError;
  firstnameLimitExceedMessage.value = false;
  lastnameLimitExceedMessage.value = false;

  const changedFields = findDifferencesInObjects(newVal, prevFormData.value);
  registerGroups.value.settings.fields.flatMap(group => group.fields.map(field => {
    if (field.required && !newVal[`reg_${field.name}`]) {
      _hasError = true;
      field.hasError = "Cannot be blank";
    } else field.hasError = null;

		if(field.type === 'custom') {
			if(newVal[`reg_${field?.name}`]?.replace(/\s+/g, '').toLowerCase() === 'other') {
				if(!newVal[`reg_${field?.name}_custom`]) {
					_hasError = true;
				}
			}
		}
  }));

  hasChanged.value = !!changedFields.length;
  hasError.value = _hasError;
}, {deep: true});

const refetchActiveTab = async () => {
  if (activeTab.value === 2) return fetchAccessMembers(1);
  if (activeTab.value === 4) return fetchAccessRequests(1);
  return getData();
};

const serverFilterOn = () => !!searchByName.value.trim() || !!renewalWithin.value || !!renewalDate.value || !!unassignedFilter.value;

const registeredFiltersOn = () => !!Object.keys(filters.value).length || serverFilterOn();

// Each tab owns its own filter, so Clear reflects and resets only that tab's —
// otherwise clearing here would silently drop a filter set on another tab.
// The broker view is not one of them: it has its own Clear beside Viewing.
const hasListFilters = computed(() => {
  if (searchByName.value.trim()) return true;
  if (activeTab.value === 4) return accessRequestsStatusFilter.value !== 'pending';
  return activeTab.value === 1 && registeredFiltersOn();
});

const hasActiveFilters = computed(() => hasListFilters.value
  || (activeTab.value !== 4 && !!brokerFilter.value));

const clearListFilters = async () => {
  const hadServerFilter = serverFilterOn();
  searchByName.value = '';

  if (activeTab.value === 2) {
    await fetchAccessMembers(1);
    return;
  }

  if (activeTab.value === 4) {
    accessRequestsStatusFilter.value = 'pending';
    await fetchAccessRequests(1);
    return;
  }

  filters.value = {};
  renewalWithin.value = '';
  renewalDate.value = '';
  unassignedFilter.value = '';
  applyRowFilters();

  // The roles filter is client-side; the search term and the renewal and broker filters are not.
  if (hadServerFilter) {
    clearMatchingSelection();
    await getData(1);
  }
};

// With no list filter on, the broker view is what emptied the list.
const clearFilters = async () => {
  if (hasListFilters.value) return clearListFilters();
  await handleBrokerScope('');
};

const handleSearchDebounce = debounce(async () => {
  await refetchActiveTab();
}, 600);

const handleSearch = async () => {
  if (activeTab.value === 2) {
    accessMembers.isLoading = true;
  } else if (activeTab.value === 4) {
    accessRequests.isLoading = true;
  } else {
    registeredUsers.isLoading = true;
    pendingUsers.isLoading = true;
  }
  await handleSearchDebounce();
};

const applyRowFilters = () => {
  const keys = Object.keys(filters.value).filter(key => key !== 'fullname');

  registeredUsers.data = registeredAllUsers.value.filter(user => {
    const conditions = [];

    keys.forEach(key => {
      if (key === 'reg_role' && filters.value[key].length > 0) {
        const rolesKey = {
          'Speaker': 'is_speaker',
          'Admin': 'is_moderator',
          'No role': 'no_role'
        };

        const hasRole = filters.value['reg_role'].some(role => user[rolesKey[role]]);
        conditions.push(hasRole);
      } else if (key === 'policy_lines') {
        // Multi-select within the filter is OR; every filter in `keys` is then
        // ANDed by the `.every()` below, so Policy Lines composes with Role and
        // the registration-field filters without special-casing them.
        const names = (user.policy_lines ?? []).map(tag => tag.name);
        conditions.push(filters.value[key].some(name => names.includes(name)));
      } else {
        conditions.push(filters.value[key].includes(user[key]));
      }
    });

    return conditions.every(v => v === true);
  });
};

const handleFilterList = (name, values) => {
  if (values.length) filters.value[name] = values;
  else delete filters.value[name];

  applyRowFilters();
};

const handleCountryChange = (fields) => {
  const stateField = fields.find(item => item.type === 'state');
  if(stateField) {
    modalData.value[`reg_${stateField.name}`] = null;
  }
};

const getData = async (page = 1) => {
  try {
    // Unfiltered by role, so this page holds everyone registered on the hub.
    // Narrowing it to attendees dropped hub staff and owners, who the access
    // picker already offers as recipients — a resource shared with one of them
    // then had no row here to be counted against.
    return await axios.get(`${store.event_backend_api}/users`, {
      params: {
        page,
        per_page: LIST_PAGE_SIZE,
        search: searchByName.value.trim(),
        ...serverFilterParams(),
        ...(renewalSort.value ? { sort: 'next_renewal', direction: renewalSort.value } : {}),
      }
    }).then(async res => {
      const {data: registerFields} = await axios.get(`${store.event_backend_api}/register-settings`);
      registerGroups.value = registerFields.data;

      // Counted by the server over the same book it just paginated, so the
      // banner and the list its button opens cannot describe different people.
      // Null is "not for this viewer", which is not the same as none.
      unassignedCount.value = res.data.unassigned ?? 0;

      // Ordering belongs to the API: this response is one page, so sorting it
      // here would rank each page independently and float that page's newest
      // row to the top of every page.
      if (res.data.registered) {
        registeredAllUsers.value = res.data.registered.data;
        registeredUsers.data = res.data.registered.data;
        registeredUsers.total = res.data.registered?.total ?? 0;
        setRegisteredTotal(registeredUsers.total);
        // The card and the Total Attendees line report the hub, not the current
        // query, so only an unfiltered response is allowed to move them.
        if (!serverFilterOn()) registeredHubTotal.value = registeredUsers.total;
        else fetchRegisteredTotal();
      }

      if (res.data.pending) {
        pendingAllUsers.value = res.data.pending.data;
        pendingUsers.data = res.data.pending.data;
        pendingUsers.total = res.data.pending?.total ?? 0;
        setPendingTotal(pendingUsers.total);
      }

      loadCount.value++;
    });
  } catch (error) {
    throw new Error(error);
  } finally {
    registeredUsers.isLoading = false;
    pendingUsers.isLoading = false;
  }
};

const requestMembers = async (page) => {
  const res = await axios.get(`${store.event_backend_api}/access/members`, {
    params: {
      page,
      per_page: 10,
      status: PENDING_TAB_STATUSES,
      ...(brokerFilter.value ? { broker_id: brokerFilter.value } : {}),
      ...(searchByName.value.trim() ? { search: searchByName.value.trim() } : {}),
    }
  });
  return res.data.data;
};

const applyMembers = (paginator) => {
  accessMembers.data = paginator.data;
  accessMembers.total = paginator.total;
  accessMembers.currentPage = paginator.current_page;
  accessMembers.lastPage = paginator.last_page;
  setMembersTotal(paginator.total);
  setMembersPage(paginator.current_page);
};

const fetchAccessMembers = latestOnly(async (page = 1) => {
  try {
    accessMembers.isLoading = true;
    return await pageOrLast(requestMembers, page);
  } catch (error) {
    console.error(error);
    return null;
  } finally {
    accessMembers.isLoading = false;
  }
}, applyMembers);

const registeredListLoadsTotal = () => activeTab.value === 1 && !serverFilterOn();

// The same request the Registered list makes, so the card and the tab read the
// same number whichever tab was opened first.
const fetchRegisteredTotal = async () => {
  const broker = brokerFilter.value;
  try {
    const res = await axios.get(`${store.event_backend_api}/users`, {
      params: {
        page: 1,
        per_page: 10,
        ...(broker ? { broker_id: broker } : {}),
      },
    });
    if (brokerFilter.value !== broker) return;
    registeredHubTotal.value = res.data.registered?.total ?? 0;
  } catch (error) {
    console.error(error);
  }
};

const handlePrevious = async () => {
  if (activeTab.value === 1) {
    setRegisteredPage(registeredPagination.currentPage - 1);
    registeredUsers.isLoading = true;
    await getData(registeredPagination.currentPage);
    return;
  }

  if (activeTab.value === 2) {
    await fetchAccessMembers(membersPagination.currentPage - 1);
    return;
  }

  if (activeTab.value === 4) {
    await fetchAccessRequests(requestsPagination.currentPage - 1);
    return;
  }

  setPendingPage(pendingPagination.currentPage - 1);
  pendingUsers.isLoading = true;
  await getData(pendingPagination.currentPage);
};

const handleNext = async () => {
  if (activeTab.value === 1) {
    setRegisteredPage(registeredPagination.currentPage + 1);
    registeredUsers.isLoading = true;
    await getData(registeredPagination.currentPage);
    return;
  }

  if (activeTab.value === 2) {
    await fetchAccessMembers(membersPagination.currentPage + 1);
    return;
  }

  if (activeTab.value === 4) {
    await fetchAccessRequests(requestsPagination.currentPage + 1);
    return;
  }

  setPendingPage(pendingPagination.currentPage + 1);
  pendingUsers.isLoading = true;
  await getData(pendingPagination.currentPage);
};

function copyEmail(email) {
  navigator.clipboard.writeText(email);
}

const router = useRouter();
const route = useRoute();
const brokerScope = useBrokerScopeStore();
const { members: hubMembers } = useHubMembers();
const memberUpload = ref(null);
const memberUploadInput = ref(null);
const memberUploadTarget = ref(null);
const memberUploadDeferred = ref(false);
const stagedResources = useStagedMemberResources();

const memberLabelFor = (user) =>
  user.fullname || `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email;

const handleMemberFilesPicked = (event) => {
  const files = [...(event.target.files || [])];
  if (!files.length || !memberUploadTarget.value) return;

  const user = memberUploadTarget.value;
  memberUpload.value = {
    member: {
      id: user.id,
      label: memberLabelFor(user),
      email: user.email || '',
      img: user.profile_image || 'Default',
    },
    files,
    deferred: memberUploadDeferred.value,
  };
};

const closeMemberUpload = () => {
  memberUpload.value = null;
  memberUploadTarget.value = null;
};

const handleMemberUploaded = async () => {
  closeMemberUpload();
  await getData(registeredPagination.currentPage);
};

const handleMemberStaged = (entry) => {
  closeMemberUpload();
  stagedResources.stage(entry);
};

function editUser (user) {
  router.push(`/policyholders/${user.id}`);
}

function openInvited (row) {
  router.push(`/policyholders/invited/${row.id}`);
}

function closeSaveUserModal () {
  modalUser.value = false;
  saveUserLoading.value = false;
  resetPolicyLineState();
  stagedResources.reset();
}
// A rejected tag write (bad label, hub at its cap) is the user's to fix, so
// surface the server's wording rather than a generic failure.
const profileSaveFailed = (error) => {
  const message = error?.response?.data?.message;
  policyLineError.value = error?.response?.status === 422 ? (message || '') : '';
  window.loadNotification({ message: message || 'Failed to update policyholder profile', error: true });
};

async function saveUser () {
  saveUserLoading.value = true;

  try {
    // Skipped when nothing on the profile itself moved, so saving after a
    // resource change alone does not write the same values back.
    if (hasChanged.value) {
      await axios.post(`${store.event_backend_api}/user-settings-update-all/attendee`, modalData.value);
    }

    // Tags are separate, tenant-admin-guarded
    // resources; neither is ever part of the settings payload above.
    await savePolicyLines(modalData.value.id);
    await saveBroker(modalData.value.id);
    // Newly created tags have to reach the filter rail without a page reload.
    await fetchPolicyLineTags();
    // Last, so a rejected profile or tag write does not leave files uploaded
    // for a member whose Save never went through.
    await stagedResources.commit();

    modalUser.value = false;
    resetPolicyLineState();
    window.loadNotification({ message: 'Policyholder profile has been updated' });
    refreshData();
  } catch (error) {
    profileSaveFailed(error);
  } finally {
    saveUserLoading.value = false;
  }
}

function retrievePassword () {
  try {
    axios.post(`${authStore.ssoBackendUrl}/api/forgot-password`, {
      email: modalData.value.email,
      redirect_url: window.location.origin
    }).then(res => {
      if (res.data.success) {
        modalUser.value = false;
        if (res.data.message) window.loadNotification({ message: res.data.message });
      }
    });
  } catch (error) {
    throw new Error(error);
  }
}

const exportModalOpen = ref(false);

// Only the columns this page can actually produce for a client-side file. The
// whole-hub file is the server's and carries its own set, which is why the
// picker is hidden for that scope rather than ignored.
const EXPORT_COLUMNS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'policy_lines', label: 'Policy lines' },
  { key: 'created_at', label: 'Registered' },
];

const runExport = async ({ scope, columns }) => {
  exportModalOpen.value = false;

  if (scope === 'all') return exportWholeList();

  const rows = scope === 'selected' ? selectedUsers.value : registeredUsers.data;
  return downloadRowsCsv(rows, columns);
};

function downloadRowsCsv (rows, columnKeys) {
  const picked = EXPORT_COLUMNS.filter((c) => columnKeys.includes(c.key));
  // Policy lines is itself comma-separated, so every cell is quoted or the
  // column count changes from row to row.
  const cell = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const valueFor = (row, key) => (key === 'policy_lines'
    ? (row.policy_lines ?? []).map((tag) => tag.name).join(', ')
    : row[key]);

  const csv = [
    picked.map((c) => cell(c.label)).join(','),
    ...rows.map((row) => picked.map((c) => cell(valueFor(row, c.key))).join(',')),
  ].join('\n');

  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  link.setAttribute('download', getExportName('Policyholder'));
  // Chrome ignores a click on an anchor that is not in the document.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

async function exportWholeList () {
  try {
    exportAllLoading.value = true;
    const response = await axios.get(`${store.event_backend_api}/users/export-csv`, {
      params: { type: activeTab.value === 2 ? 'pending' : 'registered' },
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', getExportName('Policyholder'));
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Export failed:', error);
    window.loadNotification({ message: 'Failed to export users', error: true });
  } finally {
    exportAllLoading.value = false;
  }
}

async function csvExport () {
  if (selectedUsers.value.length > 0) {
    const columns = {
      id: 'ID',
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      // ISO, matching the server-side "export all" CSV — spreadsheets import a
      // localised date as text.
      // One comma-separated column, matching the server-side "export all" CSV.
      policy_lines: 'Policy Lines',
    };

    registerGroups.value.settings.fields.map(({fields}) => {
      fields.map(field => {
        columns['reg_' + field.name] = field.title;
      });
    });

    // Policy Lines is itself comma-separated, so cells have to be quoted or the
    // column count changes per row. Applied to every cell, since a free-text
    // registration answer can contain a comma or quote just as easily.
    const csvCell = (value) => {
      const text = value === null || value === undefined ? '' : String(value);
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };

    let csvContent = 'data:text/csv;charset=utf-8,';
    const values = selectedUsers.value.map(u => {
      return Object.keys(columns).map(key => {
        if (key === 'policy_lines') return (u.policy_lines ?? []).map(tag => tag.name).join(', ');
        return u[key];
      });
    });
    csvContent += [
      Object.values(columns).map(csvCell).join(','),
      ...values.map(row => row.map(csvCell).join(',')),
    ].join('\n');

    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', getExportName('Policyholder'));
    link.click();
  }
}

function removeSelectedUsers () {
  if (modalErase.value.confirm !== 'DELETE') {
    window.loadNotification({ message: 'You must type DELETE in all caps', error: true });
  } else {
    modalErase.value.loading = true;

    axios.post(`${store.event_backend_api}/users/remove-multiple`, {
      users: selectedUsers.value.map(({id, email}) => ({id: Number(id), email}))
    }).then(() => {
      modalErase.value.loading = false;
      modalErase.value.visible = false;
      modalErase.value.confirm = '';
      window.loadNotification({ message: plural(selectedUsers.value.length, 'policyholder was deleted.', 'policyholders were deleted.') });
      refreshData();
    });
  }
}

// Returns the promise so callers can act once the rows exist — arriving with
// ?select=page needs something to tick.
function refreshData () {
  isLoading.value = true;

  return getData().then(() => {
    isLoading.value = false;
    selectedUsers.value = [];
  });
}
window.addEventListener('click', function (event) {
  const popup = document.querySelector('.uploader');
  const previewDom = document.querySelector('.preview');
  if (popup && previewDom && !previewDom.contains(event.target)) {
    if (!popup.contains(event.target)) {
      popup.classList.add('d-none');
    }
  }
});
const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}/register`);
  } catch (error) {
    console.error(error.message);
  }
};

const handleCloseAtteendeesTooltip = async () => {
  tooltipRef.value.closeTooltip();
  if (attendeesTooltipVisibility.value) {
    attendeesTooltipVisibility.value = false;
    try {
      // await axios.put(`${window.store.ajaxUrl}/user/update-data`, { isAttendeeTooltipViewed: 'true' });
    } catch (error) {
      console.error(error.message);
    }
  }
};




/**
 * Opened from the Overview's "Invite people" tile as well as from the toolbar.
 * `autofocus` on the First Name field is what makes the tile's promise true —
 * XlModal now queries `[autofocus]` before falling back to a control in its
 * body. It previously queried them as one selector list, which returns the
 * first match in tree order and therefore always handed the cursor to its own
 * ✕ button, making the attribute dead.
 */
const { cancelReturn: returnFromInvite } = useCreateOnArrival('invite', () => {
  addMemberModal.value = true;
});

const cancelAddMember = () => {
  addMemberModal.value = false;
  resetAddMemberForm();
  returnFromInvite();
};

const resetAddMemberForm = () => {
  addMember.first_name = '';
  addMember.last_name = '';
  addMember.email = '';
  addMember.company = '';
  addMember.job_title = '';
  addMember.phone = '';
  addMember.broker_id = '';
  addMember.send_invitation = false;
  addMember.loading = false;
  addMember.errors = {};
};

const PHONE_DISALLOWED = /[^0-9+()\-.\s]/g;

const sanitizePhone = (value) => {
  const cleaned = String(value ?? '').replace(PHONE_DISALLOWED, '').replace(/^\s+/, '');
  const lead = cleaned.startsWith('+') ? '+' : '';
  return lead + cleaned.replace(/\+/g, '');
};

// pge-input-text holds its own copy of the value, so rewriting only the parent
// model leaves a rejected character on screen whenever the sanitized result is
// unchanged. Rewriting the element and re-dispatching input keeps both in step.
const filterPhoneInput = (event) => {
  const el = event.target;
  const incoming = event.data ?? event.dataTransfer?.getData('text') ?? '';
  if (!incoming) return;

  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const proposed = el.value.slice(0, start) + incoming + el.value.slice(end);
  const sanitized = sanitizePhone(proposed);
  if (sanitized === proposed) return;

  event.preventDefault();
  const caret = sanitizePhone(el.value.slice(0, start) + incoming).length;
  el.value = sanitized;
  el.setSelectionRange(caret, caret);
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

const onAddMemberPhoneUpdate = (value) => {
  addMember.phone = sanitizePhone(value);
};

const isAddMemberFormValid = () => {
  const errs = {};
  if (!addMember.first_name.trim()) errs.first_name = 'First name is required';
  if (!addMember.last_name.trim()) errs.last_name = 'Last name is required';
  if (!addMember.email.trim()) {
    errs.email = 'Email is required';
  } else if (!isValidEmail(addMember.email.trim())) {
    errs.email = 'Please enter a valid email address';
  }

  // Phone is optional here, so only a filled-in field is held to the format.
  const digits = addMember.phone.replace(/\D/g, '').length;
  if (addMember.phone.trim() && (digits < 7 || digits > 15)) {
    errs.phone = 'Enter a phone number with 7 to 15 digits';
  }

  addMember.errors = errs;
  return Object.keys(errs).length === 0;
};

// Names what happened and how far it reached. "Added to the access list"
// is a different event from "we emailed them", and the checkbox above
// decides which — so the confirmation says which.
const addedMessage = (invited, who) => (invited
  ? `Invitation sent${who ? ` to ${who}` : ''}`
  : `${who || 'Policyholder'} added to the access list — no invitation sent`);

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

const addMemberRefused = (error) => {
  const responseErrors = error.response?.data?.errors || {};
  addMember.errors = Object.fromEntries(Object.entries(responseErrors).map(([key, value]) => [key, firstOf(value)]));
  window.loadNotification({ message: firstOf(Object.values(responseErrors)[0]), error: true });
};

const ADD_MEMBER_FAILURES = {
  409: () => window.loadNotification({ message: 'This policyholder is already on the access list', error: true }),
  422: addMemberRefused,
};

const addMemberFailed = (error) => {
  const handle = ADD_MEMBER_FAILURES[error.response?.status];
  if (handle) handle(error);
  else console.error(error.message);
};

const handleAddMember = async () => {
  if (!isAddMemberFormValid()) return;

  try {
    addMember.loading = true;

    await axios.post(`${store.event_backend_api}/access/members`, {
      first_name: addMember.first_name.trim(),
      last_name: addMember.last_name.trim(),
      email: addMember.email.trim(),
      company: addMember.company.trim(),
      job_title: addMember.job_title.trim(),
      phone: addMember.phone.trim(),
      broker_id: addMember.broker_id === '' ? null : Number(addMember.broker_id),
      send_invitation: addMember.send_invitation,
    });

    const invited = addMember.send_invitation;
    addMemberModal.value = false;
    resetAddMemberForm();

    const who = [addMember.first_name, addMember.last_name].filter(Boolean).join(' ').trim();
    window.loadNotification({ message: addedMessage(invited, who) });

    activeTab.value = 2;
    await refreshAccessViews();
  } catch (error) {
    addMemberFailed(error);
  } finally {
    addMember.loading = false;
  }
};

const requestCounts = async () => {
  const broker = brokerFilter.value;
  countsFailure.value = null;
  try {
    const res = await axios.get(`${store.event_backend_api}/access/counts`, {
      // The cards answer to the same filter the list does, so the two never
      // describe different populations.
      params: broker ? { broker_id: broker } : {},
    });
    return { broker, counts: res.data.data };
  } catch (error) {
    console.error(error);
    return { broker, failed: true };
  }
};

const applyCounts = ({ broker, counts }) => {
  if (brokerFilter.value === broker) {
    accessCounts.value = counts;
    countsBroker.value = broker;
    countsReady.value = true;
  }
};

const noteCountsFailure = ({ broker }) => {
  countsFailure.value = { broker };
};

const fetchAccessCounts = latestOnly(requestCounts, (reply) => (reply.failed ? noteCountsFailure(reply) : applyCounts(reply)));

const requestRequests = async (page) => {
  const res = await axios.get(`${store.event_backend_api}/access/requests`, {
    params: {
      page,
      per_page: 10,
      ...(accessRequestsStatusFilter.value ? { status: accessRequestsStatusFilter.value } : {}),
      ...(searchByName.value.trim() ? { search: searchByName.value.trim() } : {}),
    }
  });
  return res.data.data;
};

const applyRequests = (paginator) => {
  accessRequests.data = paginator.data;
  accessRequests.total = paginator.total;
  accessRequests.currentPage = paginator.current_page;
  accessRequests.lastPage = paginator.last_page;
  setRequestsTotal(paginator.total);
  setRequestsPage(paginator.current_page);
};

const fetchAccessRequests = latestOnly(async (page = 1) => {
  try {
    accessRequests.isLoading = true;
    return await pageOrLast(requestRequests, page);
  } catch (error) {
    console.error(error);
    return null;
  } finally {
    accessRequests.isLoading = false;
  }
}, applyRequests);

const refetchOpenPage = () => {
  if (activeTab.value === 2) return fetchAccessMembers(accessMembers.currentPage);
  if (activeTab.value === 4) return fetchAccessRequests(accessRequests.currentPage);
  return selectedUsers.value.length ? undefined : getData(registeredPagination.currentPage);
};

const refreshAccessViews = () => Promise.all([fetchAccessCounts(), refetchOpenPage()]);

const handleAccessRequestsStatusFilterChange = async () => {
  await fetchAccessRequests(1);
};

const getRequestStatusInfo = (status) => {
  const map = {
    pending: { label: 'Pending', tooltip: 'Non-invited users who submitted an access request awaiting review.' },
    approved: { label: 'Approved', tooltip: '' },
    rejected: { label: 'Rejected', tooltip: '' },
  };
  return map[status] || { label: status, tooltip: '' };
};

const openReview = (request) => { selectedRequest.value = request; reviewRequestModal.value = true; };
const closeReview = () => { reviewRequestModal.value = false; selectedRequest.value = null; };

const APPROVE_FAILURES = {
  409: 'This request has already been reviewed',
  502: 'Approval could not complete (policyholder provisioning failed). Please retry.',
};

// The button emits the native MouseEvent, so a bare @click binding passes one
// here and shadows the default argument. Fall back unless the caller really
// handed us a request.
const approveRequest = async (candidate = null) => {
  const req = candidate?.id ? candidate : selectedRequest.value;
  if (!req) return;
  try {
    reviewLoading.value = true;
    await axios.post(`${store.event_backend_api}/access/requests/${req.id}/approve`);
    window.loadNotification({ message: 'Request approved' });
    if (reviewRequestModal.value) closeReview();
    await refreshAccessViews();
  } catch (error) {
    const msg = APPROVE_FAILURES[error?.response?.status] ?? 'Failed to approve the request';
    window.loadNotification({ message: msg, error: true });
  } finally {
    reviewLoading.value = false;
  }
};

const rejectRequest = async (candidate = null) => {
  const req = candidate?.id ? candidate : selectedRequest.value;
  if (!req) return;
  try {
    reviewLoading.value = true;
    await axios.post(`${store.event_backend_api}/access/requests/${req.id}/reject`);
    window.loadNotification({ message: 'Request rejected' });
    if (reviewRequestModal.value) closeReview();
    await refreshAccessViews();
  } catch (error) {
    const status = error?.response?.status;
    const msg = status === 409 ? 'This request has already been reviewed' : 'Failed to reject the request';
    window.loadNotification({ message: msg, error: true });
  } finally {
    reviewLoading.value = false;
  }
};

const handleChangeActiveTab = (requestedTab) => {
  const tabId = tabFor(requestedTab, viewerIsBroker.value);
  const slug = SLUG_FOR_TAB[tabId];

  // replace, not push: switching tabs is not a place you go back to, and the
  // broker filter already in the query has to survive the write.
  if (slug && route.query.tab !== slug) {
    router.replace({ path: route.path, query: { ...route.query, tab: slug } });
  }

  activeTab.value = tabId;
};

const memberActionLoading = ref(false);

const handleResendMemberInvite = async (member) => {
  try {
    memberActionLoading.value = true;
    await axios.post(`${store.event_backend_api}/access/members/${member.id}/invite`);
    window.loadNotification({ message: sentToast(member) });
    await refreshAccessViews();
  } catch (error) {
    window.loadNotification({ message: 'Failed to send invitation.', error: true });
    console.error(error);
  } finally {
    memberActionLoading.value = false;
  }
};

const handleRevokeMember = async (member) => {
  try {
    memberActionLoading.value = true;
    await axios.post(`${store.event_backend_api}/access/members/${member.id}/revoke`);
    window.loadNotification({ message: removedToast(member) });
    await refreshAccessViews();
  } catch (error) {
    // A broker holding policyholders is refused with 422 rather than leaving a
    // book with nobody to see it. That refusal is actionable, so it opens the
    // transfer instead of reporting a dead end.
    const brokerId = brokerLocalIdFor(member);

    if (error.response?.status === 422 && brokerId !== null) {
      bookTransfer.value = { member, fromBrokerId: brokerId, target: null, message: error.response.data.message };
      return;
    }

    window.loadNotification({
      message: error.response?.data?.message || 'Failed to revoke policyholder.',
      error: true,
    });
    console.error(error);
  } finally {
    memberActionLoading.value = false;
  }
};

const brokerLocalIdFor = (member) => {
  const broker = brokers.value.find(b => Number(b.sso_user_id) === Number(member.sso_user_id));
  return broker ? broker.id : null;
};

const confirmBookTransfer = async () => {
  const { member, fromBrokerId, target } = bookTransfer.value;
  if (target === null) return;

  memberActionLoading.value = true;

  try {
    const result = await transferBook(fromBrokerId, target);
    window.loadNotification({ message: `${result.moved.length} moved to ${brokerNameById(target)}` });
    bookTransfer.value = null;

    await handleRevokeMember(member);
    await loadBrokers();
  } catch (error) {
    window.loadNotification({
      message: error.response?.data?.message || 'The book could not be transferred.',
      error: true,
    });
  } finally {
    memberActionLoading.value = false;
  }
};

const handleDeletePendingUser = async () => {
  try {

    await axios.delete(`${store.event_backend_api}/invites/${pendingDeleteUserModal.inviteId}`);

    window.loadNotification({
      message: 'Pending user deleted successfully.'
    });

    setPendingPage(1);
    await getData();
    pendingDeleteUserModal.visible = false;
    pendingDeleteUserModal.inviteId = 0;
  } catch {
    window.loadNotification({
      message: 'Failed to delete pending user.',
      error: true,
    });
  } finally {
  }
};

watch(activeTab, async (newTab) => {
  selectedUsers.value = [];

  if (newTab === 1 && !registeredAllUsers.value.length) {
    setRegisteredPage(1);
    registeredUsers.isLoading = true;
    await getData(registeredPagination.currentPage);
  } else if (newTab === 2) {
    await fetchAccessMembers(1);
  } else if (newTab === 4) {
    await Promise.all([fetchAccessRequests(1), fetchAccessCounts()]);
  }

  if (newTab === 1) {
    registeredUsers.data.forEach(user => user.selected = false);
  } else if (newTab === 2) {
    accessMembers.data.forEach(member => member.selected = false);
  }
});

const tooltipRef = ref(null);
// The list is deliberately NOT fetched here. Arriving from an Overview count
// carries filters in the URL, and this hook runs before the one that reads them
// — so it used to fire an unfiltered request that raced the filtered one, and
// the list could settle on whichever answered last.
onMounted(() => { fetchPolicyLineTags(); });

const socketStore = useSocketStore();
const authStore = useAuthStore();

const brokers = ref([]);
// Until the list is in, the Add policyholder form keeps the broker picker's
// place: it used to appear once the brokers arrived and push the invitation
// box down — opened from the Overview's Invite, while this page was still
// loading, the form was drawn twice (Agency Hub → "Invite still flickers").
const brokersLoaded = ref(false);
const brokerFilter = ref('');
// Days, or '' for no window. Set from ?renewal_within= when the Overview's
// "Renewing soon" card links here; there is no control for it on this page yet,
// so it arrives and is cleared rather than being picked.
const renewalWithin = ref('');
const renewalDate = ref('');
const unassignedFilter = ref('');

const serverFilterParams = () => ({
  ...(brokerFilter.value || unassignedFilter.value ? { broker_id: brokerFilter.value || unassignedFilter.value } : {}),
  ...(renewalWithin.value ? { renewal_within: renewalWithin.value } : {}),
  ...(renewalDate.value ? { renewal_date: renewalDate.value } : {}),
});

const handleRenewalDate = async () => {
  renewalWithin.value = '';
  clearMatchingSelection();
  await getData(1);
};

const handleUnassignedFilter = async () => {
  clearMatchingSelection();
  await getData(1);
};
// '' is the default order (newest registration first); 'asc' | 'desc' sort by
// the nearest policy renewal, which only the API can do — the response is one
// page, so ordering it here would rank each page on its own.
const renewalSort = ref('');

const handleRenewalSort = async (direction) => {
  renewalSort.value = direction;
  clearMatchingSelection();
  await getData(1);
};
const bulkAssignOpen = ref(false);
const brokerSelected = ref(null);
const prevBrokerSelected = ref(null);
const lastBatch = ref(null);
const matchingSelection = ref(null);
const matchingCount = ref(null);
const openImport = () => router.push('/policyholders/import');
const bookTransfer = ref(null);
const brokerHistoryEntries = ref([]);
let undoTimer = null;

const canAssignBrokers = computed(() => roleCanAssignBrokers(authStore.userData));
const viewerIsBroker = computed(() => roleIsBroker(authStore.userData) && !canAssignBrokers.value);

const eventStore = useEventStore();

const deckCounts = computed(() => {
  if (countsReady.value && countsBroker.value === brokerFilter.value) return accessCounts.value;

  return countsFailure.value?.broker === brokerFilter.value ? {} : null;
});

const countsPending = computed(() => deckCounts.value === null);

const deckInput = () => ({
  reader: hubReader(authStore),
  role: authStore.userRole,
  capabilities: authStore.capabilities,
  hubName: hubName(eventStore.event.value),
  counts: deckCounts.value,
  filtered: hasActiveFilters.value || Boolean(brokerFilter.value),
});

const phPageState = computed(() => (props.mini || isLoading.value ? null : hubEmptyState('policyholders', deckInput())));
const phPage = computed(() => phPageState.value !== null);
const registeredState = computed(() => hubEmptyState('registered', deckInput()));

const PAGE_ACTIONS = {
  invite: () => { addMemberModal.value = true; },
  import: () => openImport(),
};
const onPageAction = (id) => PAGE_ACTIONS[id]?.();

const brokerNameById = (id) => {
  const broker = brokers.value.find(b => b.id === Number(id));
  return broker ? `${broker.first_name ?? ''} ${broker.last_name ?? ''}`.trim() || broker.email : '';
};

// The directory carries SSO name parts; the picker wants a person to show.
const brokerChoices = computed(() => brokers.value.map(b => ({ id: b.id, name: brokerNameById(b.id) })));

const loadBrokers = async () => {
  try {
    brokers.value = await listBrokers();
  } catch (error) {
    console.error(error);
  } finally {
    brokersLoaded.value = true;
  }
};

// v-model has already written the selected value; the change payload is the
// component's own event object, so assigning it here would clobber the filter.
const handleBrokerFilter = async () => {
  clearMatchingSelection();
  // Whichever tab is open — the filter applies to the pending list too now, so
  // refetching only the registered one left tab 2 showing the previous broker's
  // people.
  await refetchActiveTab();
};

// Counted by the server across the whole book rather than the page on screen,
// which holds ten rows and would understate it everywhere but the last page.
// It arrives with the list itself — see getData().
//
// Remembered for the session and drawn at once while the list is on its way:
// the banner otherwise arrived with the list and pushed the filters, already
// on screen, 105px down ("Policyholders jumps"). Only the first visit of a
// session can still move, once, as the list lands.
const UNASSIGNED_KEY = 'policyholders:unassigned';
const rememberedUnassigned = () => {
  try {
    return Number(sessionStorage.getItem(UNASSIGNED_KEY)) || 0;
  } catch {
    return 0;
  }
};
const unassignedCount = ref(rememberedUnassigned());
watch(unassignedCount, (count) => {
  try {
    sessionStorage.setItem(UNASSIGNED_KEY, String(count ?? 0));
  } catch {
    // A per-tab convenience: nothing breaks without it.
  }
});

const showUnassigned = async () => {
  unassignedFilter.value = 'none';
  await handleUnassignedFilter();
};

const selectedAlreadyAssigned = computed(
  () => selectedUsers.value.filter((user) => user.broker_id).length
);

// A deep watcher over the page derives selectedUsers from row.selected, so the
// flag on the row is the source of truth the bulk actions read.
const toggleRowSelection = (row) => { row.selected = !row.selected; };

const togglePageSelection = (on) => {
  registeredUsers.data.forEach((row) => { row.selected = on; });
};

/**
 * `?select=page` from the Overview's unassigned links.
 *
 * The point of arriving at the unassigned list is to assign somebody, and the
 * bulk bar does not exist until a row is ticked — so without this, "opens with
 * selection enabled" meant three clicks of preamble. Ticks what actually
 * loaded; the existing "select all matching" affordance then offers the rest.
 */
const selectOnArrival = ref(null);

const applySelectionFromUrl = () => {
  if (selectOnArrival.value !== 'page') return;

  selectOnArrival.value = null;
  togglePageSelection(true);
};

const clearSelection = () => {
  togglePageSelection(false);
  clearMatchingSelection();
};

const handleRowMessage = (row) => {
  router.push({
    path: '/messages',
    query: { ...brokerScope.query, thread: String(row.id) },
  });
};

const handleBulkMessage = () => {
  router.push({ path: '/communications', query: { tab: 'send' } });
};

const handleBrokerScope = async (value) => {
  brokerFilter.value = value === '' ? null : value;
  unassignedFilter.value = '';
  // Also into the store and the URL: this page used to read ?broker_id on
  // arrival but never write it, so the filter died on the way back to the
  // Overview.
  brokerScope.set(value, router, route);
  // Off the Registered tab the list is not refetched, so drop it and let the
  // tab watcher load the new broker's people when it is opened.
  if (activeTab.value !== 1) registeredAllUsers.value = [];
  await Promise.all([
    handleBrokerFilter(),
    fetchAccessCounts(),
    ...(registeredListLoadsTotal() ? [] : [fetchRegisteredTotal()]),
  ]);
};

// Each summary card stands for one of the tabs beneath it, so clicking a number
// opens the list it counts rather than only reporting it.
const KPI_TABS = { 'access-granted': 1, registered: 1, 'not-registered': 2, requests: 4 };

const handleKpiSelect = (key) => {
  handleChangeActiveTab(KPI_TABS[key] ?? 1);
};

async function saveBroker (userId) {
  if (brokerSelected.value === prevBrokerSelected.value) return;

  await assignBroker(userId, brokerSelected.value);
  await loadBrokers();
}

const openBulkAssign = () => { bulkAssignOpen.value = true; };

const currentListFilters = () => ({
  search: searchByName.value.trim(),
  ...serverFilterParams(),
});

// The page's checkbox selects the ten rows on screen; this selects everything
// behind the filter and says how many that really is, which is usually a much
// larger number than the one the user just ticked.
const selectAllMatching = async () => {
  try {
    const { total, selection, truncated } = await matchingPolicyholders(currentListFilters());

    matchingSelection.value = selection;
    matchingCount.value = total;

    if (truncated) {
      window.loadNotification({
        message: `Selecting the first ${selection.length} of ${total}. Narrow the filters to act on the rest.`,
        type: 'error',
      });
    }
  } catch (error) {
    console.error(error);
    window.loadNotification({ message: 'Could not work out how many match those filters', type: 'error' });
  }
};

const clearMatchingSelection = () => {
  matchingSelection.value = null;
  matchingCount.value = null;
};

// The toast's Undo is the only place a bulk mistake can be caught cheaply, so
// the window is held open for 30 seconds and then dropped.
const handleBulkAssigned = async (result) => {
  bulkAssignOpen.value = false;
  lastBatch.value = result.batch_id;

  if (undoTimer) clearTimeout(undoTimer);
  undoTimer = setTimeout(() => { lastBatch.value = null; }, 30000);

  // Partial failures never reach here: BulkAssignModal keeps itself open and
  // names them rather than emitting a success that was only partly true.
  window.loadNotification({
    message: `${result.moved.length} moved to ${result.brokerName}`,
  });

  clearMatchingSelection();
  selectedUsers.value = [];
  await Promise.all([getData(registeredPagination.currentPage), loadBrokers()]);
};

const handleUndoBulk = async () => {
  if (!lastBatch.value) return;

  try {
    const { reverted } = await undoBulkAssign(lastBatch.value);
    window.loadNotification({ message: `${reverted} put back` });
  } catch {
    window.loadNotification({ message: 'That could not be undone', type: 'error' });
  } finally {
    lastBatch.value = null;
    if (undoTimer) clearTimeout(undoTimer);
    await Promise.all([getData(registeredPagination.currentPage), loadBrokers()]);
  }
};

const applyTabFromUrl = (slug) => {
  if (!TAB_SLUGS[slug]) return;

  const tab = tabFor(TAB_SLUGS[slug], viewerIsBroker.value);
  activeTab.value = tab;

  if (tab !== TAB_SLUGS[slug]) {
    router.replace({ path: route.path, query: { ...route.query, tab: SLUG_FOR_TAB[tab] } });
  }
};

// Every count on the Overview links here with its filters in the URL, and the
// list has to contain exactly the people that number counted — so the filters
// are applied before the first fetch, not after it.
const applyFiltersFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const broker = params.get('broker_id');
  const search = params.get('search');
  const renewal = params.get('renewal_within');

  if (broker !== null) brokerFilter.value = broker;
  if (search !== null) searchByName.value = search;
  if (renewal !== null) renewalWithin.value = renewal;
  applyTabFromUrl(params.get('tab'));

  // Deliberately NOT part of the return value below, which means "did a filter
  // arrive" and drives whether the list refetches. Selection is not a filter.
  selectOnArrival.value = params.get('select');

  return broker !== null || search !== null || renewal !== null;
};

onMounted(async () => {
  // The URL is authoritative on arrival; the store carries the filter between
  // pages, where a sidebar click has no query to carry it.
  brokerScope.adoptFromRoute(route);

  const arrived = applyFiltersFromUrl();

  if (!arrived && brokerScope.brokerId) {
    brokerFilter.value = brokerScope.brokerId;
  }

  fetchAccessCounts();
  if (!registeredListLoadsTotal()) fetchRegisteredTotal();

  await loadBrokers();

  // The one fetch of the list on arrival, after the filters are in place, so
  // what renders is what the URL asked for.
  if (activeTab.value === 1) {
    // Awaited, because the selection below can only tick rows that exist.
    await refreshData();
    applySelectionFromUrl();
  } else {
    // refetchActiveTab() only toggles its own tab's isLoading (e.g.
    // accessMembers.isLoading) — the page-level flag below is what gates the
    // whole template (see `<loading v-if="isLoading" />`), and refreshData()
    // is the only other place that clears it. Without this, arriving on any
    // tab but the first (e.g. Overview's "Never signed in" -> ?tab=pending)
    // left the page stuck behind the skeleton forever.
    await refetchActiveTab();
    isLoading.value = false;
  }
});

// A same-route query change — the back button between two filtered views, or a
// second click on a count — does not remount the component, so without this the
// address bar would move and the list would not.
watch(() => route.query, () => {
  if (applyFiltersFromUrl()) refetchActiveTab();
});

const HUB_ACCESS_ROOM_DOMAIN = window.store?.subdomain || 'default';

const refreshOnPush = debounce(() => Promise.all([refreshAccessViews(), fetchRegisteredTotal()]), 300);

function handleHubAccessCounts(payload) {
  if (!payload || payload.domain !== HUB_ACCESS_ROOM_DOMAIN) return;
  refreshOnPush();
}

function subscribeHubAccess() {
  socketStore.emit('hub_access:subscribe', { domain: HUB_ACCESS_ROOM_DOMAIN });
  socketStore.off('hub_access:counts_changed', handleHubAccessCounts);
  socketStore.on('hub_access:counts_changed', handleHubAccessCounts);
}

let releaseViewing = () => {};

onMounted(async () => {
  releaseViewing = viewingPolicyholders();
  await socketStore.connect();
  // on() no-ops until the socket instance exists, so wait for it if connect is still pending.
  if (socketStore.socket) {
    subscribeHubAccess();
  } else {
    const stopWatch = watch(() => socketStore.socket, (s) => {
      if (s) { subscribeHubAccess(); stopWatch(); }
    });
  }
});

onUnmounted(() => {
  releaseViewing();
  if (!canAssignBrokers.value) {
    socketStore.emit('hub_access:unsubscribe', { domain: HUB_ACCESS_ROOM_DOMAIN });
  }
  socketStore.off('hub_access:counts_changed', handleHubAccessCounts);
});
</script>

<template>
  <!--
    The page is drawn while its data is on the way: heading, buttons, counts,
    tabs and filters in place, placeholders only where numbers and rows go
    (Policyholders: a bare table skeleton under the header, so the table
    landed ~450px lower and everything jumped — XN-4902 AC4).
  -->
    <PageHeader style="z-index: 51;">
      <template #left>
        <tooltip-info
          ref="tooltipRef"
          :is-visible="attendeesTooltipVisibility"
          custom
          persistent
          title="Policyholders Page"
          text="Policyholders will appear here once they are registered for your hub."
          x-position="left"
          @close="handleCloseAtteendeesTooltip"
        >
        <template #actions>
          <xl-button variant="ghost" size="sm" @click="handleCopy"><template #icon><icon-copy /></template>Copy Registration Link</xl-button>
          <xl-button variant="ghost" @click="handleCloseAtteendeesTooltip">Okay</xl-button>
        </template>
      </tooltip-info>
      </template>

    </PageHeader>
      <!--
        The generation boundary sits on the padded wrapper, as it does on
        Communications and Messages. On the inner element the page's paper
        stopped short of the 40px padding, so the redesign read as an inset
        panel floating on the shell's older grey.
      -->
      <div class="page-content" data-xl-gen="northstar">
      <div>
        <div class="page-users">
          <export-list-modal
            v-if="exportModalOpen"
            :total-count="registeredHubTotal"
            :filtered-count="registeredUsers.data.length"
            :selected-count="selectedUsers.length"
            :filters-on="hasActiveFilters"
            :columns="EXPORT_COLUMNS"
            @close="exportModalOpen = false"
            @export="runExport"
          />

          <div class="page-users__page">
            <header class="page-users__head">
              <div class="page-users__heading">
                <p>{{ canAssignBrokers
                  ? 'Everyone with access to your portal.'
                  : 'The people you look after.' }}</p>
              </div>

              <xl-button
                variant="ghost"
                v-if="canAssignBrokers"
                data-testid="import-policyholders-button"
                @click="openImport"
              >
                <template #icon>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 3v12M7.5 11l4.5 4.5 4.5-4.5M4 20h16" />
                  </svg>
                </template>
                Import
              </xl-button>
              <xl-button
                variant="ghost"
                data-testid="export-list-button"
                :loading="exportAllLoading"
                @click="exportModalOpen = true"
              >Export as list</xl-button>
              <xl-button data-testid="add-policyholder-button" @click="addMemberModal = true">
                <template #icon>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                  </svg>
                </template>
                Add policyholder
              </xl-button>
            </header>

            <ReleasedBookNotice
              v-if="canAssignBrokers"
              :brokers="brokers"
              @settled="() => Promise.all([getData(registeredPagination.currentPage), loadBrokers()])"
            />

            <div v-if="(activeTab === 1 || activeTab === 2) && !phPage" class="page-users__viewing">
              <span>Viewing</span>
              <broker-scope-picker
                :can-choose="canAssignBrokers"
                :brokers="brokerChoices"
                :book-size="countsLoading ? null : registeredHubTotal"
                :model-value="brokerFilter ?? ''"
                @update:model-value="handleBrokerScope"
              />
            </div>

            <template v-if="!mini && !phPage">
              <policyholder-kpi-row
                :access-granted="accessCounts.access_granted ?? 0"
                :not-registered="accessCounts.not_registered ?? 0"
                :registered="registeredHubTotal"
                :waiting-for-approval="accessCounts.pending_requests ?? 0"
                :show-requests="!viewerIsBroker"
                :scoped-to-broker="Boolean(brokerFilter)"
                :loading="countsLoading"
                @select="handleKpiSelect"
              />
            </template>

            <XlEmptyState
              v-if="phPageState"
              variant="page"
              :icon="phPageState.icon"
              :headline="phPageState.headline"
              :body="phPageState.body"
              :actions="phPageState.actions"
              data-testid="policyholders-empty"
              @action="onPageAction"
            />

            <template v-if="(!mini || registeredAllUsers.length || pendingAllUsers.length) && !phPage">
              <div class="page-users__table">
                <div class="page-users__table__container">
                  <xl-tabs
                    :tabs="USER_MODAL_TABS"
                    :model-value="activeTab"
                    @update:model-value="handleChangeActiveTab"
                  />
                  <div class="policyholders__panel">
                    <loading-overlay v-if="!isLoading && (registeredUsers.isLoading || accessMembers.isLoading || accessRequests.isLoading)" style="border-radius: 12px;" />

                    <unassigned-broker-banner
                      v-if="activeTab === 1 && canAssignBrokers"
                      :count="unassignedCount"
                      @show="showUnassigned"
                    />

              <div class="policyholders__filters" v-if="!mini">
                <xl-search-field
                  class="policyholders__search"
                  placeholder="Search by name or email"
                  aria-label="Search policyholders by name or email"
                  :model-value="searchByName"
                  @update:model-value="(value) => { searchByName = value; handleSearch(); }"
                />
                <xl-multi-select
                  v-if="activeTab === 1"
                  :options="['Speaker', 'Admin', 'No role']"
                  placeholder="All roles"
                  aria-label="Filter by role"
                  :model-value="filters.reg_role || []"
                  @update:model-value="(values) => handleFilterList('reg_role', values)"
                />
                <xl-dropdown
                  v-if="activeTab === 4"
                  placeholder="All statuses"
                  aria-label="Filter by status"
                  v-model="accessRequestsStatusFilter"
                  :options="[
                    { label: 'All statuses', value: '' },
                    { label: 'Pending', value: 'pending' },
                    { label: 'Approved', value: 'approved' },
                    { label: 'Rejected', value: 'rejected' },
                  ]"
                  @change="handleAccessRequestsStatusFilterChange"
                />
                <xl-multi-select
                  v-if="activeTab === 1 && (!policyLineTagsLoaded || policyLineFilterOptions.length)"
                  :options="policyLineFilterOptions"
                  placeholder="All policy lines"
                  searchable
                  aria-label="Filter by policy line"
                  :model-value="filters.policy_lines || []"
                  @update:model-value="(values) => handleFilterList('policy_lines', values)"
                />
                <xl-dropdown
                  v-if="activeTab === 1"
                  placeholder="Any renewal date"
                  aria-label="Filter by renewal date"
                  v-model="renewalDate"
                  :options="[
                    { label: 'Any renewal date', value: '' },
                    { label: 'Coming up', value: 'coming_up' },
                    { label: 'Just passed', value: 'just_passed' },
                    { label: 'Not set', value: 'not_set' },
                  ]"
                  @change="handleRenewalDate"
                />
                <xl-dropdown
                  v-if="activeTab === 1 && canAssignBrokers && !brokerFilter"
                  placeholder="Any renewal"
                  aria-label="Filter by broker"
                  v-model="unassignedFilter"
                  :options="[
                    { label: 'Any renewal', value: '' },
                    { label: 'No broker', value: 'none' },
                  ]"
                  @change="handleUnassignedFilter"
                />
                <xl-multi-select
                  v-for="field in (activeTab === 1 ? fields : [])"
                  :key="field.name"
                  :options="field.options"
                  :placeholder="field.title"
                  :aria-label="`Filter by ${field.title}`"
                  :model-value="filters[field.reg_name] || []"
                  @update:model-value="(values) => handleFilterList(field.reg_name, values)"
                />
                <xl-button v-if="hasListFilters" variant="ghost" size="sm" @click="clearListFilters">Clear</xl-button>
              </div>
                    <template v-if="activeTab === 1">
                      <div
                        v-if="canAssignBrokers && selectedUsers.length && registeredUsers.total > selectedUsers.length"
                        class="select-all-matching"
                        data-testid="select-all-matching"
                      >
                        <template v-if="matchingCount === null">
                          <span>{{ selectedUsers.length }} on this page selected.</span>
                          <button type="button" @click="selectAllMatching">
                            Select all {{ registeredUsers.total }} matching this filter
                          </button>
                        </template>
                        <template v-else>
                          <span data-testid="matching-selected">All {{ matchingCount }} matching your filters selected.</span>
                          <button type="button" @click="clearMatchingSelection">Clear selection</button>
                        </template>
                      </div>

                      <!-- A broker's empty list means their book is empty, not the hub. Saying
                           "none for this hub" to them is simply untrue. -->
                      <template v-if="isLoading || (!registeredUsers.data.length && countsPending)">
                        <p class="policyholders__hint"><xl-skeleton width="320px" height="0.85em" radius="6px" class="policyholders__hint-sk" /></p>
                        <xl-card class="policyholders__table-card">
                          <policyholder-table loading :loading-rows="loadingRowCount" :show-broker="canAssignBrokers" :renewal-sort="renewalSort" />
                        </xl-card>
                      </template>
                      <XlEmptyState
                        v-else-if="registeredState && !registeredUsers.data.length && !registeredUsers.isLoading && !hasActiveFilters"
                        :headline="registeredState.headline"
                        :body="registeredState.body"
                        :actions="registeredState.actions"
                        :data-state="registeredState.key"
                        data-testid="registered-empty"
                        @action="handleChangeActiveTab(2)"
                      />
                      <template v-else-if="registeredUsers.data.length || !registeredUsers.isLoading">
                        <policyholder-bulk-bar
                          :count="selectedUsers.length"
                          :already-assigned="selectedAlreadyAssigned"
                          :can-assign-brokers="canAssignBrokers"
                          @assign="openBulkAssign"
                          @message="handleBulkMessage"
                          @export="csvExport"
                          @remove="modalErase.visible = true"
                          @cancel="clearSelection"
                        />

                        <p v-if="!selectedUsers.length" class="policyholders__hint">
                          {{ registeredUsers.data.length }} of {{ registeredUsers.total }} shown{{ hasListFilters ? ' \u2014 filters are on' : '' }}.
                          Tick rows to {{ canAssignBrokers ? 'assign a broker, ' : '' }}message or export them together.
                        </p>

                        <xl-card class="policyholders__table-card">
                          <policyholder-table
                            :rows="registeredUsers.data"
                            :selected-ids="selectedUsers.map(u => u.id)"
                            :show-broker="canAssignBrokers"
                            :broker-name-by-id="brokerNameById"
                            :renewal-sort="renewalSort"
                            @sort="handleRenewalSort"
                            @open="editUser"
                            @message="handleRowMessage"
                            @toggle="toggleRowSelection"
                            @toggle-all="togglePageSelection"
                          >
                            <template #empty>
                              <span data-testid="empty-filtered">
                                Nobody matches those filters.
                                <button type="button" class="page-users__clear-inline" data-testid="empty-clear-filters" @click="clearFilters">Clear them</button>.
                              </span>
                            </template>
                          </policyholder-table>
                        </xl-card>
                      </template>
                    </template>

                    <loading v-else-if="isLoading" variant="table" :rows="6" />

                    <template v-else-if="activeTab === 2">
                      <p class="policyholders__hint">
                        {{ accessMembers.data.length }} of {{ accessMembers.total }} shown{{ hasListFilters ? ' \u2014 filters are on' : '' }}.
                      </p>
                      <policyholder-invitations
                        :rows="accessMembers.data"
                        :busy="memberActionLoading"
                        :show-broker="canAssignBrokers"
                        :broker-name-by-id="brokerNameById"
                        :never-told="accessCounts.never_told ?? 0"
                        :filtered="hasListFilters"
                        @resend="handleResendMemberInvite"
                        @revoke="handleRevokeMember"
                        @open="openInvited"
                      />
                    </template>

                    <template v-else-if="activeTab === 4">
                      <p class="policyholders__hint">
                        {{ accessRequests.data.length }} of {{ accessRequests.total }} shown{{ hasListFilters ? ' \u2014 filters are on' : '' }}.
                      </p>
                      <policyholder-requests
                        :rows="accessRequests.data"
                        :busy="memberActionLoading"
                        :show-broker="canAssignBrokers"
                        :broker-name-by-id="brokerNameById"
                        @approve="approveRequest"
                        @decline="rejectRequest"
                        @view="openReview"
                      />
                    </template>
                  </div>
                </div>
              </div>

              <xl-pagination
                v-if="activeTab === 1 ? registeredUsers.data.length : (activeTab === 2 ? accessMembers.data.length : accessRequests.data.length)"
                :from="activeTab === 1 ? registeredFrom : (activeTab === 2 ? membersFrom : requestsFrom)"
                :to="activeTab === 1 ? registeredTo : (activeTab === 2 ? membersTo : requestsTo)"
                :total="pagedTotal"
                :selected="activeTab === 1 ? selectedUsers.length : 0"
                class="page-users__footer"
                @previous="handlePrevious"
                @next="handleNext"
              />
            </template>
          </div>

          <xl-modal
            v-if="pendingDeleteUserModal.visible"
            max-width="312px"
            @close="() => pendingDeleteUserModal.visible = !pendingDeleteUserModal.visible"
          >
              <div class="delete__modal__content">
                <div class="delete__modal__content__icon">
                  <icon-user-remove size="32" />
                </div>

                <h2>Remove pending invite?</h2>

                <p>This will permanently remove the pending invite. This action cannot be undone.</p>
              </div>
                        <template #actions>
              <div class="delete__modal__actions">
                <xl-button variant="ghost" @click="() => pendingDeleteUserModal.visible = false">Cancel</xl-button>
                <xl-button variant="danger" :loading="pendingDeleteUserModal.loading" @click="handleDeletePendingUser">Remove</xl-button>
              </div>
            </template>
          </xl-modal>

          <xl-modal
            v-if="modalErase.visible"
            title="Delete Selected Policyholders"
            bordered
            @close="() => modalErase.visible = !modalErase.visible"
          >
              <xl-field v-model="modalErase.confirm" type="text" name="confirm" label="Type “DELETE” below to confirm" />
              <br />
              <p>You have selected {{ plural(selectedUsers.length, 'policyholder') }} to delete.</p>
                        <template v-slot:actions>
              <xl-button variant="ghost" @click="() => modalErase.visible = false">Cancel</xl-button>
              <xl-button variant="danger" :loading="modalErase.loading" @click="removeSelectedUsers">Delete</xl-button>
            </template>
          </xl-modal>

          <xl-modal
            v-if="modalUser"
            title="Edit Policyholder"
            bordered
            @close="closeSaveUserModal"
          >
              <div class="form-title">Policyholder Information</div>
              <div class="form-group">
                <div class="row row--next">
                  <div class="profile_image--wrapper column position-relative">
                    <span>Photo</span>
                    <div
                      class="image"
                      :style="{
                        width: userProfileInfo.profile_image ? '56px' : 'auto',
                        height: userProfileInfo.profile_image ? '56px' : 'auto',
                      }"
                    >
                      <img
                        v-if="userProfileInfo.profile_image"
                        :src="userProfileInfo.profile_image"
                        :alt="userProfileInfo.first_name"
                      />
                      <span v-else class="default-avatar">
                        <icon-default-avatar size="56" />
                      </span>
                    </div>
                  </div>
                  <div class="column" style="width: 100%;">
                    <span class="field-caption">Roles</span>
                    <div class="row gap-1">
                      <div class="column" v-if="modalData.is_speaker">
                        <div class="chip-item">Speaker</div>
                      </div>
                      <div class="column" v-if="modalData.is_moderator">
                        <div class="chip-item">Admin</div>
                      </div>
                      <div class="column" v-if="modalData.no_role">
                        <div class="chip-item">No Role</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <xl-field type="text" name="first_name" label="First Name" v-model="userProfileInfo.first_name" disabled />
                <div class="span text-danger" v-if="firstnameLimitExceedMessage">Character limit for First Name should be 50 max</div>
              </div>
              <div class="form-group">
                <xl-field type="text" name="last_name" label="Last Name" v-model="userProfileInfo.last_name" disabled />
                <div class="span text-danger" v-if="lastnameLimitExceedMessage">Character limit for Last Name should be 50 max</div>
              </div>
              <div class="form-group">
                <span class="field-caption">Email</span>
                <div class="text-content">
                  {{ modalData.email }}
                  <div class="copy-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" @click="copyEmail(modalData.email)">
                      <path d="M16.875 2.5H6.875C6.70924 2.5 6.55027 2.56585 6.43306 2.68306C6.31585 2.80027 6.25 2.95924 6.25 3.125V6.25H3.125C2.95924 6.25 2.80027 6.31585 2.68306 6.43306C2.56585 6.55027 2.5 6.70924 2.5 6.875V16.875C2.5 17.0408 2.56585 17.1997 2.68306 17.3169C2.80027 17.4342 2.95924 17.5 3.125 17.5H13.125C13.2908 17.5 13.4497 17.4342 13.5669 17.3169C13.6842 17.1997 13.75 17.0408 13.75 16.875V13.75H16.875C17.0408 13.75 17.1997 13.6842 17.3169 13.5669C17.4342 13.4497 17.5 13.2908 17.5 13.125V3.125C17.5 2.95924 17.4342 2.80027 17.3169 2.68306C17.1997 2.56585 17.0408 2.5 16.875 2.5ZM12.5 16.25H3.75V7.5H12.5V16.25ZM16.25 12.5H13.75V6.875C13.75 6.70924 13.6842 6.55027 13.5669 6.43306C13.4497 6.31585 13.2908 6.25 13.125 6.25H7.5V3.75H16.25V12.5Z" fill="#4B444D"/>
                    </svg>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <span class="field-caption">Policy Lines</span>
                <pge-multiselect
                  creatable
                  name="policy_lines"
                  placeholder="Search or create a tag"
                  :model="policyLineSelected"
                  v-model="policyLineSelected"
                  :options="policyLineOptions"
                  :chip-colors="policyLineChipColors"
                  :empty-state-text="POLICY_LINE_EMPTY_STATE"
                  options-heading="Existing tags in this hub"
                  :error-text="policyLineError"
                  :validate-label="validatePolicyLineLabel"
                  @create="handleCreatePolicyLine"
                />
                <div class="policy-line-help">
                  Tags describe what this policyholder is enrolled in. Press Enter or comma to add.
                </div>
              </div>

              <div class="form-group" v-if="canAssignBrokers || viewerIsBroker">
                <BrokerCard
                  v-model="brokerSelected"
                  :brokers="brokers"
                  :original-broker-id="prevBrokerSelected"
                  :person-name="`${userProfileInfo.first_name ?? ''} ${userProfileInfo.last_name ?? ''}`.trim()"
                  :can-assign="canAssignBrokers"
                  :history="brokerHistoryEntries"
                />
              </div>

              <div class="form-group">
                <span class="field-caption">Password</span>
                <xl-button variant="ghost" size="sm" @click="retrievePassword">Send Password Reset Link</xl-button>
              </div>

              <form-group v-for="(group, groupIndex) in registerGroups.settings.fields" :key="groupIndex" :title="group.title">
                <template v-for="(field, fieldIndex) in group.fields" :key="fieldIndex">
                  <template v-if="field.type === 'text'">
                    <xl-field :maxlength="300" type="text" :label="field.title" v-model="modalData[`reg_${field.name}`]" :required="field.required" :invalid="!!field.hasError" />
                  </template>
                  <template v-if="field.type === 'country'">
                    <form-dropdown
                      :model="modalData[`reg_${field.name}`]"
                      labelType="separated"
                      v-model="modalData[`reg_${field.name}`]"
                      :label="field.title"
                      :name="field.name"
                      :options="registerGroups.countries.map(item => ({ label: item, value: item }))"
                      @change="handleCountryChange(group.fields)"
                      :required="field.required"
                      :has-required-sign="field.required"
                    />
                  </template>
                  <template v-if="field.type === 'state'">
                    <form-dropdown
                      v-if="group.fields.find(item => item.type === 'country') && modalData[`reg_${group.fields.find(item => item.type === 'country').name}`]"
                      :model="modalData[`reg_${field.name}`]"
                      v-model="modalData[`reg_${field.name}`]"
                      :label="field.title"
                      labelType="separated"
                      :disabled="modalData[`reg_${group.fields.find(item => item.type === 'country').name}`] ? false : true"
                      :name="field.name"
                      :options="registerGroups.regions[modalData[`reg_${group.fields.find(item => item.type === 'country').name}`]].map(item => ({ label: item, value: item }))"
                      :required="field.required"
                      :has-required-sign="field.required"
                    />
                    <form-dropdown-group
                      v-if="!group.fields.find(item => item.type === 'country')"
                      :model="modalData[`reg_${field.name}`]"
                      v-model="modalData[`reg_${field.name}`]"
                      :label="field.title"
                      labelType="separated"
                      :name="field.name"
                      :options="Object.entries(registerGroups.regions).reduce((acc, [country, items]) => {
                        acc[country] = items.map(item => ({ label: item, value: item }));
                        return acc;
                      }, {})"
                      required
                    />
                  </template>
                  <template v-if="field.type === 'policy_line'">
                    <form-dropdown
                      :model="modalData[`reg_${field.name}`]"
                      v-model="modalData[`reg_${field.name}`]"
                      :label="field.title"
                      labelType="separated"
                      :name="field.name"
                      :options="answerOptions(policyLineTags, modalData[`reg_${field.name}`])"
                      :required="field.required"
                      :has-required-sign="field.required"
                    />
                  </template>
                  <template v-if="field.type === 'custom'">
                    <form-dropdown
                      :model="modalData[`reg_${field.name}`]"
                      v-model="modalData[`reg_${field.name}`]"
                      :label="field.title"
                      labelType="separated"
                      :name="field.name"
                      :options="field.options.split(',').map(item => ({ label: item, value: item }))"
                      :required="field.required"
                      :has-required-sign="field.required"
                    />
                  </template>
                  <div style="margin-top: 15px" v-if="!['text', 'policy_line'].includes(field.type) && modalData[`reg_${field.name}`]?.replace(/\s+/g, '').toLowerCase() === 'other'">
                    <xl-field :maxlength="300" type="text" :label="`Your ${field.title}`" :name="`${field.name}_custom`" v-model="modalData[`reg_${field.name}_custom`]" :required="field.required" :invalid="!!field.hasError" />
                  </div>
                  <span
                    v-if="field.hasError && ['custom', 'policy_line', 'state', 'country'].includes(field.type)"
                    class="form-group__error-text"
                  >
                    {{ field.hasError }}
                  </span>
                </template>
              </form-group>

                        <template v-slot:actions>
              <xl-button variant="ghost" @click="closeSaveUserModal" :loading="saveUserLoading">Close</xl-button>
              <xl-button @click="saveUser" :loading="saveUserLoading" :disabled="hasError || (!hasChanged && !policyLinesChanged && !brokerChanged && !stagedResources.hasPending.value)">Save</xl-button>
            </template>
          </xl-modal>

          <xl-modal
            v-if="reviewRequestModal"
            title="Policyholder Request Details"
            @close="closeReview"
          >
              <div class="review-request__content">
                <div class="review-request__field-row">
                  <span class="review-request__label">First Name</span>
                  <span class="review-request__value">{{ selectedRequest?.first_name || '—' }}</span>
                </div>
                <div class="review-request__field-row">
                  <span class="review-request__label">Last Name</span>
                  <span class="review-request__value">{{ selectedRequest?.last_name || '—' }}</span>
                </div>
                <div class="review-request__field-row">
                  <span class="review-request__label">Email</span>
                  <span class="review-request__value">{{ selectedRequest?.email || '—' }}</span>
                </div>
                <div class="review-request__field-row">
                  <span class="review-request__label">Phone</span>
                  <span class="review-request__value">{{ selectedRequest?.phone || '—' }}</span>
                </div>
                <div class="review-request__field-row">
                  <span class="review-request__label">Submitted</span>
                  <span class="review-request__value">{{ longDate(selectedRequest?.created_at) || '—' }}</span>
                </div>
                <div class="review-request__field-row">
                  <span class="review-request__label">Status</span>
                  <span class="review-request__value">
                    <span class="access-member-status__badge" :class="`access-member-status__badge--${selectedRequest?.status}`">{{ getRequestStatusInfo(selectedRequest?.status).label }}</span>
                  </span>
                </div>
                <div v-if="selectedRequest?.notes" class="review-request__field-row review-request__field-row--notes">
                  <span class="review-request__label">Notes</span>
                  <span class="review-request__value review-request__value--notes">{{ selectedRequest.notes }}</span>
                </div>

                <div v-if="selectedRequest?.status === 'pending'" class="review-request__on-approval">
                  <strong class="review-request__on-approval-title">On Approval</strong>
                  <ul class="review-request__on-approval-list">
                    <li>{{ selectedRequest.first_name }} {{ selectedRequest.last_name }} is granted access.</li>
                    <li>Approval email sent automatically to {{ selectedRequest.email }}.</li>
                    <li>They can register and access the Hub.</li>
                  </ul>
                </div>
              </div>
                        <template #actions>
              <xl-button variant="ghost" @click="closeReview">Close</xl-button>
              <template v-if="selectedRequest?.status === 'pending'">
                <xl-button variant="ghost-danger" :disabled="reviewLoading" @click="() => rejectRequest()">Reject</xl-button>
                <xl-button variant="success" :loading="reviewLoading" :disabled="reviewLoading" @click="() => approveRequest()">Approve</xl-button>
              </template>
            </template>
          </xl-modal>

          <xl-modal
            v-if="addMemberModal"
            title="Add Policyholder"
            @close="cancelAddMember"
          >
              <div class="add__member__content">
                <p class="add__member__section-label">Policyholder Information</p>

                <div class="add__member__row">
                  <xl-field v-model="addMember.first_name" type="text" name="first_name" label="First Name" required autofocus :error="addMember.errors.first_name || ''" />
                  <xl-field v-model="addMember.last_name" type="text" name="last_name" label="Last Name" required :error="addMember.errors.last_name || ''" />
                </div>

                <xl-field v-model="addMember.email" type="text" name="email" label="Email" required :error="addMember.errors.email || ''" />

                <xl-field v-model="addMember.company" type="text" name="company" label="Company Name" />

                <div class="add__member__row">
                  <xl-field v-model="addMember.job_title" type="text" name="job_title" label="Job Title" />
                  <xl-field
                  :model-value="addMember.phone"
                  type="tel"
                  name="phone"
                  label="Phone Number"
                  :error="addMember.errors.phone || ''"
                  @update:model-value="onAddMemberPhoneUpdate"
                  @beforeinput="filterPhoneInput"
                />
                </div>

                <div class="add__member__broker" v-if="canAssignBrokers && (brokers.length || !brokersLoaded)">
                  <form-dropdown
                    name="broker_id"
                    label="Their broker"
                    labelType="separated"
                    data-testid="add-member-broker"
                    :model="addMember.broker_id"
                    v-model="addMember.broker_id"
                    :options="[{ label: '— Nobody yet', value: '' }, ...brokers.map(b => ({ label: brokerNameById(b.id), value: b.id }))]"
                  />
                  <p class="add__member__broker-help">
                    Choose now and they arrive owned by someone. Leave it empty and nobody is watching their renewal.
                  </p>
                </div>

                <div class="add__member__broker" v-else-if="viewerIsBroker">
                  <span class="add__member__section-label field-caption">Their broker</span>
                  <p class="add__member__broker-help" data-testid="add-member-broker-self">
                    They will arrive assigned to you.
                  </p>
                </div>

                <div class="add__member__invite-box">
                  <div class="add__member__invite-box__header">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="add__member__invite-box__icon">
                      <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9.75 12.75H8.25V8.25H9.75V12.75ZM9.75 6.75H8.25V5.25H9.75V6.75Z" fill="#5E2F7B"/>
                    </svg>
                    <span class="add__member__invite-box__title">Send Invitation Email</span>
                  </div>
                  <p class="add__member__invite-box__desc">Send an invitation with the Hub name, description, registration link, and contact information.</p>
                  <div class="add__member__checkbox">
                    <input
                      id="send_invitation"
                      type="checkbox"
                      v-model="addMember.send_invitation"
                    />
                    <label for="send_invitation">Send invitation email immediately</label>
                  </div>
                </div>
              </div>
            
            <template #actions>
              <xl-button variant="ghost" :disabled="addMember.loading" @click="cancelAddMember">Cancel</xl-button>
              <xl-button :loading="addMember.loading" :disabled="addMember.loading" @click="handleAddMember">Grant Access</xl-button>
            </template>
          </xl-modal>
        </div>
      </div>
    </div>

    <!-- Mounted beside the page, not inside it, so they take the generation here. -->
    <div data-xl-gen="northstar" class="page-users__dialogs">
    <BulkAssignModal
      v-if="bulkAssignOpen"
      :selection="matchingSelection ?? selectedUsers"
      :brokers="brokers"
      @close="bulkAssignOpen = false"
      @assigned="handleBulkAssigned"
    />

    <xl-modal
      v-if="bookTransfer"
      title="Move their book first"
      max-width="520px"
      :on-close="() => (bookTransfer = null)"
      @close="bookTransfer = null"
    >
        <div class="book-transfer" data-testid="book-transfer">
          <p>{{ bookTransfer.message }}</p>
          <BrokerPicker
            v-model="bookTransfer.target"
            :brokers="brokers.filter(b => b.id !== bookTransfer.fromBrokerId)"
          />
        </div>
      
      <template #actions>
        <xl-button variant="ghost" size="sm" @click="bookTransfer = null">Cancel</xl-button>
        <xl-button size="sm" :disabled="!bookTransfer.target || memberActionLoading" :loading="memberActionLoading" data-testid="book-transfer-confirm" @click="confirmBookTransfer">{{ bookTransfer.target ? `Move the book to ${brokerNameById(bookTransfer.target).split(' ')[0]}` : 'Pick a broker' }}</xl-button>
      </template>
    </xl-modal>
    </div>


    <div v-if="lastBatch" class="broker-undo" data-testid="broker-undo">
      <span>Assignment saved.</span>
      <button type="button" @click="handleUndoBulk">Undo</button>
    </div>

    <input
      ref="memberUploadInput"
      type="file"
      aria-label="Files to upload"
      accept="application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/jpeg,image/png,image/gif,text/plain,application/pdf"
      multiple
      hidden
      @change="handleMemberFilesPicked"
      @click="$event.target.value = null"
    />

    <upload-for-member-modal
      v-if="memberUpload"
      :member="memberUpload.member"
      :files="memberUpload.files"
      :members="hubMembers"
      :deferred="memberUpload.deferred"
      @cancel="closeMemberUpload"
      @uploaded="handleMemberUploaded"
      @staged="handleMemberStaged"
    />
</template>

<style lang="scss" scoped>
// Gives the dialogs the page's generation without a box of its own: the
// generation rule paints a background and a min-height on whatever carries it.
.page-users__dialogs { display: contents; }

.broker-cell {
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-primary-text);

  // Unassigned is a normal state, not an error: neutral, never red.
  &--none { color: var(--xl-color-secondary-text); }
}

.add__member__broker {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.add__member__broker-help {
  margin: 0;
  font-family: var(--xl-font-family);
  font-size: var(--xl-font-size-sm);
  color: var(--xl-color-secondary-text);
}


.book-transfer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);
  line-height: var(--xl-type-body-medium-line);
  color: var(--xl-color-primary-text);

  p { margin: 0; }
}

.select-all-matching {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: var(--xl-radius-md);
  background: var(--xl-color-primary-container);
  color: var(--xl-color-on-primary-container);
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);

  button {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    font-weight: var(--xl-font-weight-bold);
    text-decoration: underline;
    cursor: pointer;
  }
}

.broker-undo {
  position: fixed;
  left: 50%;
  bottom: 24px;
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 14px;
  transform: translateX(-50%);
  padding: 12px 18px;
  border-radius: var(--xl-radius-sm);
  box-shadow: var(--xl-shadow-md);
  background: var(--xl-color-on-surface);
  color: var(--xl-color-surface);
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);

  button {
    background: none;
    border: 0;
    color: var(--xl-color-primary-fixed-dim);
    font-family: inherit;
    font-size: var(--xl-type-body-medium-size);
    font-weight: var(--xl-font-weight-bold);
    cursor: pointer;
  }
}
  :deep(.d-container label > span.floated) {
    background-color: #fff !important;
  }
  .access-kpi-row {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
  }
  .access-kpi-card {
    flex: 1;
    background: #fff;
    border: 1px solid #E5E5E5;
    border-radius: 12px;
    padding: 20px 24px;

    &__value {
      font-size: 32px;
      font-weight: 700;
      line-height: 1.1;

      &--alert {
        color: #C0392B;
      }
    }

    &__label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: #6B7280;
      margin-top: 4px;
    }

    &__info {
      display: inline-flex;
      align-items: center;
      cursor: help;
    }
  }
  .two-lines-overflow-width {
    max-width: 150px;
    min-width: 80px;
  }
  .page-content {
    &__footer {
      position: sticky;
      bottom: 10px;
      width: max-content;
      margin-left: auto;
      margin-top: auto;
    }
  }
  .position-relative {
    position: relative;
  }
  .chip-item {
    display: flex;
    border-radius: 8px;
    gap: var(--spacing-1, 8px);
    padding: 6px var(--spacing-2, 16px);
    border: 1px solid var(--XL-M-Theme-sys-light-outline, #7D747E);
    height: 32px;
    justify-content: center;
    align-items: center;

    color: var(--XL-M-Theme-sys-light-on-surface-variant, #4B444D);
    text-align: center;
    font-feature-settings: 'liga' off;

    font-family: Lato, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.1px;

    &__disabled {
      color: var(--XL-M-Theme-sys-light-on-surface, #1E1A1F);
      opacity: 0.38;
      border-radius: 8px;
      border: 1px solid var(--xlm-theme-state-layers-light-on-surface-variant-opacity-012, rgba(75, 68, 77, 0.12));
    }
  }
  .d-none {
    display: none;
  }

.form-group {
  background-color: white;
  margin-bottom: 15px;

  &__error-text {
    color: #b3261e;
    margin-bottom: 10px;
    margin-top: -18px;
    font-size: 12px;
    display: block;
  }
}
.ml-1 {
  margin-left: 1em;
}
.text-content {
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  color: var(--XL-M-Theme-sys-light-on-surface, #1E1A1F);
  font-feature-settings: 'clig' off, 'liga' off;
  text-overflow: ellipsis;

  font-family: Lato, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}
.copy-icon {
  display: flex;
  padding: 5px;
  border-radius: 50%;
}
.copy-icon:hover {
  background: rgba(73, 69, 79, 0.12);
}
.pge-cb-h-container {
  height: 100% !important;
}
.blank-page {
  display: flex;
  align-items: center;
  justify-content: center;

  h2 {
    margin: 24px 0;
    font-size: 20px;
    font-weight: 400;
  }
}
.text-danger {
  color:#EA1252;
}
label,
.field-caption {
  display: block;
  color: var(--XL-M-Theme-sys-light-on-surface-variant, #4B444D);
  margin: 10px 0;
  font-feature-settings: 'clig' off, 'liga' off;

  font-family: Lato, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.4px;

  // font-weight: 400;
  // font-size: 12px;
  // line-height: 17px;
  // margin: 0 0 13px;
  // display: flex;
  // align-items: center;
}
.field-caption {
  cursor: default;
}
.w-100 {
  width: 100%;
}
.form-title {
  color: var(--XL-M-Theme-sys-light-on-surface, #1E1A1F);
  font-feature-settings: 'liga' off;

  font-family: Lato, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.1px;
  margin-bottom: 20px;
}
.row {
  display: flex !important;
  align-items: center;
  width: auto;

  &--next {
    column-gap: 15px;

    .profile_image--wrapper {
      display: flex;
      flex-direction: column;
      row-gap: 4px;
      flex: 1;
      position: relative;
      width: 100%;
      height: 100%;
      margin-bottom: 8px;

      span {
        font-size: 12px;
      }

      .image {
        border-radius: 50%;
        overflow: hidden;
        border: 1px solid #CEC3CE;

        img {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          object-fit: cover;
        }

        .default-avatar {
          display: flex;
          height: 56px;
          width: 56px;
          border-radius: 50%;
        }
      }

    }
  }

  .gap-1 {
    gap: 5px;
  }
}
// The way out of an empty result belongs in the message, not only in a
// control above it that the reader has already scrolled past.
.page-users__viewing {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  > span {
    font-family: var(--xl-font-family);
    font-size: 13.5px;
    color: var(--xl-color-outline);
  }
}

.page-users__clear-inline {
  border: 0;
  padding: 0;
  background: none;
  font: inherit;
  color: var(--xl-color-primary);
  cursor: pointer;
}

.page-users__page {
  display: flex;
  flex-direction: column;
}

// The prototype puts the page on the ground, not inside a card, with the
// buttons on the title's own line.
.page-users__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.page-users__heading {
  flex: 1;
  min-width: 230px;

  p {
    margin: 0;
    font-family: var(--xl-font-family);
    font-size: 15px;
    color: var(--xl-color-outline);
  }
}

.page-users {
  position: relative;
  margin-bottom: 40px;

  .filters {
    width: 200px;
    padding-right: 24px;
  }

  &__total_attendees {
    color: #000;
    font-feature-settings: 'clig' off, 'liga' off;

    font-family: Lato, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    width: var(--spacing-20, 160px);
  }
  &__table {
    display: flex;
    align-items: stretch;
    margin-top: 24px;

    .filters {
      position: relative;

      &-overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(255, 255, 255, 0.6);
        z-index: 3;
      }
    }

    &__container {
      position: relative;
      width: 100%;
      padding-left: 1px;
      min-height: 200px;

      :deep(.tabs__content) {
        position: relative;
        min-height: 200px;
      }
    }

    table{
      position: relative;

      &::before {
        content: ''; /* Required for pseudo-element */
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 1px; /* Width of the vertical line */
        background-color: #CEC3CE; /* Color of the vertical line */
        z-index: 2;
      }
    }
  }

  .d-container {
    min-height: 40px !important;

    & input {
      font-size: 16px;
      line-height: 24px;
      font-weight: 400;
      color: #4B444D;
    }
  }

  .page-users__footer {
    // The pager is inline-flex, so it shrink-wraps on its own and auto margins
    // never resolve — the width and auto margins that used to sit here did
    // nothing. This is the panel's own 16px rhythm, carried to the one block
    // that sits outside it.
    margin-top: 16px;
  }

  &--no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    margin: 24px 0;
    font-size: 20px;
    font-weight: 400;
  }
}

table {
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;

  thead {
    tr {
      border-bottom: 1px solid var(--XL-M-Theme-sys-light-outline-variant, #CEC3CE);
    }
  }

  th,
  td {
    text-align: left;
    padding: 10px;
    font-size: 14px;
    font-style: normal;
    line-height: 1;

    &:last-child {
      text-align: center;
    }

    a {
      display: inline-block;

      :is(img) {
        display: block;
        margin: 4px 0 0 0;
        padding: 0;
      }
    }
  }

  td {
    font-weight: 400;

    .user-image {
      border-radius: 50%;
      overflow: hidden;
      width: 29px;
      height: 29px;

      img {
        object-fit: cover;
      }
    }
  }

  th {
    padding: 10px;
    font-weight: 700;
    background-color: #fff;
    position: sticky;
    top: 0;
    z-index: 1;

    &:first-child {
      padding-top: 0px;
    }
  }

  .actions {
    display: flex;

    a,
    span {
      cursor: pointer;
    }

    span {
      margin: 0 6px;
    }

    a:last-child {
      color: #EA1252;
    }
  }
}
.btn-neutral-danger {
  display: flex;
  padding: 10px var(--spacing-2, 16px) 10px 0;
  align-items: center;
  gap: var(--spacing-1, 8px);
  align-self: stretch;
  cursor: pointer;
  color: var(--XL-M-Theme-sys-light-error, #BA1A1A);
  text-align: center;
  font-feature-settings: 'liga' off;

  font-family: Lato, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;


  &.disabled {
    opacity: 0.38;
    cursor: not-allowed;

    .btn-neutral-danger__text {
      color: #1E1A1F;
    }
  }
}
.reverse-mt {
  margin-bottom: -30px;
}
.policyholders__filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.policyholders__search { flex: 1; min-width: 220px; }

.export-all-row { white-space: nowrap; }

/* One rhythm for the whole page, so no two blocks sit flush against each other. */
.policyholders__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.policyholders__hint {
  margin: 0;
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

/* On the hint's own line, at its text's height. */
.policyholders__hint-sk { width: auto; vertical-align: middle; }

/* Cards carry their own bottom margin; inside the panel the gap owns the
   spacing, so it would otherwise be counted twice. */
.policyholders__panel :deep(.xl-card) { margin-bottom: 0; }

.policyholders__table-card { padding: 0; overflow: hidden; }
.export-all-label {
  color: var(--xl-color-outline);
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-small-size);
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
}
.td-sm {
  width: 50px;
}

.first-char-container {
  display: flex;
  width: 76px;
  align-items: center;
  gap: var(--spacing-1, 8px);

  &__item {
    display: flex;
    width: 16px;
    // height: 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    border-radius: 4px;
    border: 1px solid var(--XL-M-Theme-sys-light-outline, #7D747E);
    cursor: default;

    color: var(--XL-M-Theme-sys-light-on-surface-variant, #4B444D);
    text-align: center;
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: Lato, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 133.333% */
    letter-spacing: 0.4px;
  }
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.add__member__content {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  background-color: #FFFFFF;

  &-description {
    margin: 0;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    color: #4B444D;
  }
}

.add__member__checkbox {
  display: flex;
  align-items: center;
  column-gap: 8px;
  font-size: 14px;
  color: #1E1A1F;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    margin: 0;
  }
}

.delete__modal__content {
  display: flex;
  flex-direction: column;
  row-gap: 12px;

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  h2 {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 400;
    color: #1E1A1F;
    text-align: center;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #4B444D;
  }
}

.delete__modal__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.access-members-filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  max-width: 240px;
}

.access-member-status {
  display: flex;
  align-items: center;
  gap: 6px;

  &__badge {
    display: inline-block;
    padding: 6px 16px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    white-space: nowrap;

    &--invited {
      background-color: #FFF3CD;
      color: #856404;
    }

    &--invitation_sent {
      background-color: #CCE5FF;
      color: #004085;
    }

    &--registered {
      background-color: #D4EDDA;
      color: #155724;
    }

    &--access_revoked {
      background-color: #F8D7DA;
      color: #721C24;
    }

    &--pending {
      background-color: #FDF3E2;
      color: #8A6D1A;
    }

    &--approved {
      background-color: #EAF6EE;
      color: #1E7B4F;
    }

    &--rejected {
      background-color: #FCEBEA;
      color: #C0392B;
    }
  }

  &__tooltip-trigger {
    display: inline-flex;
    align-items: center;
    cursor: default;
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }
  }
}

/* ── Add Member modal ── */
.add__member__section-label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: #7D747E;
  letter-spacing: 0.4px;
}

.add__member__row {
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
    min-width: 0;
  }
}

.add__member__invite-box {
  background: #F7ECF6;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    flex-shrink: 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: #5E2F7B;
  }

  &__desc {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #4B444D;
  }
}


/* ── Member Request Details panel ── */
.review-request__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-request__field-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &--notes {
    align-items: flex-start;
  }
}

.review-request__label {
  flex: 0 0 130px;
  font-size: 14px;
  color: #7D747E;
  line-height: 1.5;
}

.review-request__value {
  flex: 1;
  font-size: 14px;
  color: #1E1A1F;
  line-height: 1.5;

  &--notes {
    white-space: pre-wrap;
  }
}

.review-request__on-approval {
  background: #EAF6EE;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.review-request__on-approval-title {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1E7B4F;
  margin-bottom: 8px;
}

.review-request__on-approval-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #1E1A1F;

  li {
    margin-bottom: 4px;
    line-height: 1.5;
  }
}




/* ── Access Requests tab inline actions ── */
.access-request-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.request-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.request-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #DCEBFB;
  color: #2A6BB0;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
}

.ar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  font-family: Lato, sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  line-height: 1.4;
  white-space: nowrap;

  &--approve {
    background-color: #1E7B4F;
    color: #fff;

    &:hover {
      background-color: #175e3c;
    }
  }

  &--reject {
    background-color: transparent;
    border: 1px solid #C0392B;
    color: #C0392B;

    &:hover {
      background-color: #FCEBEA;
    }
  }

  &--view {
    background-color: transparent;
    color: #5E2F7B;
    padding: 4px 6px;

    &:hover {
      text-decoration: underline;
    }
  }

  // Fixed width so the trailing trash icon lines up across rows regardless of
  // the "Send Invite" vs "Resend" label width.
  &--member-action {
    min-width: 88px;
    justify-content: flex-start;
  }
}

// --- Policy Lines column -------------------------------------------------
.policy-line-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  row-gap: 4px;
  // Three chips plus a "+N" stay on one line whenever the column has room — the
  // table's natural width is unchanged by this, measured identical in Chrome.
  //
  // But `nowrap` also meant the column could not give up a single pixel, which
  // made the WHOLE table rigid at 1138px. Below that — a laptop viewport, once
  // the admin sidebar takes its ~265px — the table had no way to fit and pushed
  // the page into horizontal overflow, shifting the header and sidebar out of
  // alignment. There is no horizontal scroll here to absorb that (the table's
  // sticky header rules it out), so the columns themselves have to yield.
  //
  // Wrapping drops the table's floor to 995px: chips fold onto a second line
  // only when the width genuinely is not there, which is strictly better than a
  // broken page. Row heights stay uniform at every width that fits.
  flex-wrap: wrap;
  max-width: 260px;
}

.policy-line-chip {
  display: inline-block;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: #F3E8FD;
  color: #5E2F78;
  font-family: Lato, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;

  &--more {
    flex-shrink: 0;
    max-width: none;
    background-color: #EFF1F4;
    color: #41474F;
    cursor: default;
  }
}

.policy-line-empty {
  color: #79747E;
  font-family: Lato, sans-serif;
  font-size: 13px;
  white-space: nowrap;
}

.policy-line-help {
  margin-top: 6px;
  color: #49454F;
  font-family: Lato, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}

// Sortable header: a real button so it is keyboard-reachable, styled to sit
// flush with the plain `th` labels around it. It wraps onto a second line the
// way "Date Registered" beside it does, rather than holding the column open at
// the full width of its label.
.th-sort {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;

  &__arrow {
    font-size: 10px;
    opacity: 0.45;
  }

  &--active .th-sort__arrow {
    opacity: 1;
    color: #5E2F78;
  }
}

.member-resources-divider {
  height: 1px;
  margin: 18px 0;
  background: #EEEDF4;
}
</style>
